window.FoxTheme = window.FoxTheme || {};
FoxTheme.config = {
  hasLocalStorage: false,
  mqlMobile: false,
  mqlTablet: false,
  mediaQueryMobile: 'screen and (max-width: 767px)',
  mediaQueryTablet: 'screen and (max-width: 1023px)',
  motionReduced: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  isTouch:
    'ontouchstart' in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0,
  rtl: document.documentElement.getAttribute('dir') === 'rtl',
};

(function () {
  const _isDev =
    window.location.hostname.includes('myshopify.com') || window.location.hostname === 'localhost';
  const _err = _isDev ? console.error.bind(console) : () => {};

  // Detect browser has support local storage.
  try {
    const key = 'sleek:test';
    window.localStorage.setItem(key, 'test');
    window.localStorage.removeItem(key);
    FoxTheme.config.hasLocalStorage = true;
  } catch (_err) {}

  // Enterprise Fix: Global error handler for loading state recovery
  window.addEventListener('error', function (_event) {
    // Reset all stuck loading states on unhandled JavaScript errors
    try {
      document
        .querySelectorAll('[data-type="add-to-cart-form"] .btn--loading')
        .forEach(function (btn) {
          btn.classList.remove('btn--loading');
          btn.removeAttribute('aria-disabled');
        });

      document.querySelectorAll('[data-type="add-to-cart-form"]').forEach(function (form) {
        if (form.isSubmitting) {
          form.isSubmitting = false;
        }
      });
    } catch (_err) {
      // Ignore errors in error handler
    }
  });

  FoxTheme.DOMready = function (callback) {
    document.readyState !== 'loading'
      ? callback()
      : document.addEventListener('DOMContentLoaded', callback);
  };

  FoxTheme.a11y = {
    trapFocusHandlers: {},
    getFocusableElements: (container) => {
      return Array.from(
        container.querySelectorAll(
          "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
        )
      );
    },
    trapFocus: (container, elementToFocus = container) => {
      const elements = FoxTheme.a11y.getFocusableElements(container);
      const first = elements[0];
      const last = elements[elements.length - 1];

      FoxTheme.a11y.removeTrapFocus();

      FoxTheme.a11y.trapFocusHandlers.focusin = (event) => {
        if (event.target !== container && event.target !== last && event.target !== first) return;

        document.addEventListener('keydown', FoxTheme.a11y.trapFocusHandlers.keydown);
      };

      FoxTheme.a11y.trapFocusHandlers.focusout = function () {
        document.removeEventListener('keydown', FoxTheme.a11y.trapFocusHandlers.keydown);
      };

      FoxTheme.a11y.trapFocusHandlers.keydown = function (event) {
        if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
        // On the last focusable element and tab forward, focus the first element.
        if (event.target === last && !event.shiftKey) {
          event.preventDefault();
          first.focus();
        }

        //  On the first focusable element and tab backward, focus the last element.
        if ((event.target === container || event.target === first) && event.shiftKey) {
          event.preventDefault();
          last.focus();
        }
      };

      document.addEventListener('focusout', FoxTheme.a11y.trapFocusHandlers.focusout);
      document.addEventListener('focusin', FoxTheme.a11y.trapFocusHandlers.focusin);

      elementToFocus.focus();

      if (
        elementToFocus.tagName === 'INPUT' &&
        ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
        elementToFocus.value
      ) {
        elementToFocus.setSelectionRange(0, elementToFocus.value.length);
      }
    },
    removeTrapFocus: (elementToFocus = null) => {
      document.removeEventListener('focusin', FoxTheme.a11y.trapFocusHandlers.focusin);
      document.removeEventListener('focusout', FoxTheme.a11y.trapFocusHandlers.focusout);
      document.removeEventListener('keydown', FoxTheme.a11y.trapFocusHandlers.keydown);

      if (elementToFocus) elementToFocus.focus();
    },
  };

  FoxTheme.utils = {
    throttle: (callback) => {
      let requestId = null,
        lastArgs;
      const later = (context) => () => {
        requestId = null;
        callback.apply(context, lastArgs);
      };
      const throttled = (...args) => {
        lastArgs = args;
        if (requestId === null) {
          requestId = requestAnimationFrame(later(this));
        }
      };
      throttled.cancel = () => {
        cancelAnimationFrame(requestId);
        requestId = null;
      };
      return throttled;
    },
    setScrollbarWidth: () => {
      const scrollbarWidth = window.innerWidth - document.body.clientWidth;
      scrollbarWidth > 0 &&
        document.documentElement.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);
    },
    waitForEvent: (element, eventName) => {
      return new Promise((resolve) => {
        // Event handler that checks if the event target is the expected element
        const eventHandler = (event) => {
          if (event.target === element) {
            element.removeEventListener(eventName, eventHandler); // Clean up listener
            resolve(event); // Resolve the promise with the event
          }
        };

        // Attach the event handler to the element
        element.addEventListener(eventName, eventHandler);
      });
    },
    queryDomNodes: (selectors = {}, context = document) => {
      const domNodes = Object.entries(selectors).reduce((acc, [name, selector]) => {
        const findOne = typeof selector === 'string';
        const queryMethod = findOne ? 'querySelector' : 'querySelectorAll';
        const sl = findOne ? selector : selector[0];

        acc[name] = context && context[queryMethod](sl);
        if (!findOne && acc[name]) {
          acc[name] = [...acc[name]];
        }
        return acc;
      }, {});
      return domNodes;
    },
    addEventDelegate: ({
      context = document.documentElement,
      event = 'click',
      selector,
      handler,
      capture = false,
    }) => {
      const listener = function (e) {
        // loop parent nodes from the target to the delegation node
        for (let target = e.target; target && target !== this; target = target.parentNode) {
          if (target.matches(selector)) {
            handler.call(target, e, target);
            break;
          }
        }
      };
      context.addEventListener(event, listener, capture);
      return () => {
        context.removeEventListener(event, listener, capture);
      };
    },
    getSectionId: (element) => {
      if (element.hasAttribute('data-section-id')) {
        return element.dataset.sectionId;
      } else {
        if (!element.classList.contains('shopify-section')) {
          element = element.closest('.shopify-section');
        }
        if (!element) return null;
        return element.id.replace('shopify-section-', '');
      }
    },
    debounce: (fn, wait) => {
      let t;
      return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), wait);
      };
    },
    fetchConfig: (type = 'json') => {
      return {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
      };
    },
    /**
     * Store key-value pair with expiration time in localStorage
     * Use storage instead of cookie to make it work properly on Safari IOS (in iframe).
     */
    setStorage(key, value, expiryInDays) {
      if (!FoxTheme.config.hasLocalStorage) return;

      const now = new Date();
      const item = {
        value: value,
        expiry: now.getTime() + expiryInDays * 86400000,
      };

      try {
        window.localStorage.setItem(key, JSON.stringify(item));
      } catch (error) {
        if (error.name === 'QuotaExceededError' || error.name === 'NS_ERROR_DOM_QUOTA_REACHED') {
          if (window.CartDebug) console.warn('[Storage] Quota exceeded, attempting cleanup...');

          // Try to remove expired entries to free space
          try {
            let removedCount = 0;
            for (let i = window.localStorage.length - 1; i >= 0; i--) {
              const k = window.localStorage.key(i);
              if (!k) continue;

              try {
                const itemStr = window.localStorage.getItem(k);
                if (itemStr) {
                  const stored = JSON.parse(itemStr);
                  if (now.getTime() > stored.expiry) {
                    window.localStorage.removeItem(k);
                    removedCount++;
                  }
                }
              } catch (_e) {
                // Skip malformed entries
              }
            }

            if (removedCount > 0) {
              // Retry after cleanup
              try {
                window.localStorage.setItem(key, JSON.stringify(item));
              } catch (_retryError) {
                if (window.CartDebug) console.warn('[Storage] Still over quota after cleanup');
              }
            }
          } catch (cleanupError) {
            if (window.CartDebug) console.warn('[Storage] Error during cleanup:', cleanupError);
          }
        } else {
          _err('[Storage] Error setting item:', error);
        }
      }
    },
    getStorage(key) {
      if (!FoxTheme.config.hasLocalStorage) return null;

      try {
        const itemStr = window.localStorage.getItem(key);
        // If the item doesn't exist, return null.
        if (!itemStr) {
          return null;
        }

        let item;
        try {
          item = JSON.parse(itemStr);
        } catch (parseError) {
          // Corrupted data - remove it and return null
          if (window.CartDebug) {
            console.warn(`[Storage] Corrupted data for key "${key}":`, parseError);
          }
          try {
            window.localStorage.removeItem(key);
          } catch (_e) {
            // Ignore removal errors
          }
          return null;
        }

        const now = new Date();
        // Compare the expiry time of the item with the current time.
        if (now.getTime() > item.expiry) {
          // If the item has expired, remove it from storage and return null.
          window.localStorage.removeItem(key);
          return null;
        }
        return item.value;
      } catch (error) {
        // Unexpected error accessing localStorage
        _err(`[Storage] Error reading key "${key}":`, error);
        return null;
      }
    },
    postLink: (path, options) => {
      options = options || {};
      const method = options['method'] || 'post';
      const params = options['parameters'] || {};

      const form = document.createElement('form');
      form.setAttribute('method', method);
      form.setAttribute('action', path);

      for (const key in params) {
        for (const index in params[key]) {
          for (const key2 in params[key][index]) {
            const hiddenField = document.createElement('input');
            hiddenField.setAttribute('type', 'hidden');
            hiddenField.setAttribute('name', `${key}[${index}][${key2}]`);
            hiddenField.setAttribute('value', params[key][index][key2]);
            form.appendChild(hiddenField);
          }
        }
      }
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    },
    imageReady: (imageOrArray) => {
      if (!imageOrArray) {
        return Promise.resolve();
      }
      imageOrArray = imageOrArray instanceof Element ? [imageOrArray] : Array.from(imageOrArray);
      return Promise.all(
        imageOrArray.map((image) => {
          return new Promise((resolve) => {
            if ((image.tagName === 'IMG' && image.complete) || !image.offsetParent) {
              resolve();
            } else {
              image.onload = () => resolve();
            }
          });
        })
      );
    },
    displayedMedia: (media) => {
      return Array.from(media).find((item) => {
        const style = window.getComputedStyle(item);
        return style.display !== 'none';
      });
    },
  };

  FoxTheme.pubsub = {
    PUB_SUB_EVENTS: {
      cartUpdate: 'cart-update',
      quantityUpdate: 'quantity-update',
      variantChange: 'variant-change',
      cartError: 'cart-error',
      facetUpdate: 'facet-update',
    },
    subscribers: {},
    subscribe: (eventName, callback) => {
      if (FoxTheme.pubsub.subscribers[eventName] === undefined) {
        FoxTheme.pubsub.subscribers[eventName] = [];
      }

      FoxTheme.pubsub.subscribers[eventName] = [
        ...FoxTheme.pubsub.subscribers[eventName],
        callback,
      ];

      return function unsubscribe() {
        FoxTheme.pubsub.subscribers[eventName] = FoxTheme.pubsub.subscribers[eventName].filter(
          (cb) => {
            return cb !== callback;
          }
        );
      };
    },

    publish: (eventName, data) => {
      if (FoxTheme.pubsub.subscribers[eventName]) {
        FoxTheme.pubsub.subscribers[eventName].forEach((callback) => {
          callback(data);
        });
      }
    },
  };

  FoxTheme.focusVisiblePolyfill = function () {
    const navKeys = [
      'ARROWUP',
      'ARROWDOWN',
      'ARROWLEFT',
      'ARROWRIGHT',
      'TAB',
      'ENTER',
      'SPACE',
      'ESCAPE',
      'HOME',
      'END',
      'PAGEUP',
      'PAGEDOWN',
    ];
    let currentFocusedElement = null;
    let mouseClick = null;

    window.addEventListener('keydown', (event) => {
      if (navKeys.includes(event.code.toUpperCase())) {
        mouseClick = false;
      }
    });

    window.addEventListener('mousedown', (_event) => {
      mouseClick = true;
    });

    window.addEventListener(
      'focus',
      () => {
        if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

        if (mouseClick) return;

        currentFocusedElement = document.activeElement;
        currentFocusedElement.classList.add('focused');
      },
      true
    );
  };

  FoxTheme.Carousel = (function () {
    class Carousel {
      constructor(container, options, modules = null) {
        this.container = container;
        let defaultModules = [FoxTheme.Swiper.Navigation, FoxTheme.Swiper.Pagination];
        if (modules) {
          defaultModules = defaultModules.concat(modules);
        }
        this.options = {
          modules: defaultModules,
          ...options,
        };
      }

      init() {
        this.slider = new FoxTheme.Swiper.Swiper(this.container, this.options);
      }
    }
    return Carousel;
  })();

  FoxTheme.delayUntilInteraction = (function () {
    class ScriptLoader {
      constructor(callback, delay = 5000) {
        this.loadScriptTimer = setTimeout(callback, delay);
        this.userInteractionEvents = [
          'mouseover',
          'mousemove',
          'keydown',
          'touchstart',
          'touchend',
          'touchmove',
          'wheel',
        ];

        this.onScriptLoader = this.triggerScriptLoader.bind(this, callback);
        this.userInteractionEvents.forEach((event) => {
          window.addEventListener(event, this.onScriptLoader, {
            passive: !0,
          });
        });
      }

      triggerScriptLoader(callback) {
        callback();
        clearTimeout(this.loadScriptTimer);
        this.userInteractionEvents.forEach((event) => {
          window.removeEventListener(event, this.onScriptLoader, {
            passive: !0,
          });
        });
      }
    }

    return ScriptLoader;
  })();

  FoxTheme.Currency = (function () {
    // eslint-disable-next-line no-template-curly-in-string
    const moneyFormat = '${{amount}}';

    function formatMoney(cents, format) {
      if (typeof cents === 'string') {
        cents = cents.replace('.', '');
      }
      let value = '';
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const formatString = format || moneyFormat;

      function formatWithDelimiters(number, precision, thousands, decimal) {
        thousands = thousands || ',';
        decimal = decimal || '.';

        if (isNaN(number) || number === null) {
          return 0;
        }

        number = (number / 100.0).toFixed(precision);

        const parts = number.split('.');
        const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
        const centsAmount = parts[1] ? decimal + parts[1] : '';

        return dollarsAmount + centsAmount;
      }

      switch (formatString.match(placeholderRegex)[1]) {
        case 'amount':
          value = formatWithDelimiters(cents, 2);
          break;
        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0);
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, 0, '.', ',');
          break;
        case 'amount_no_decimals_with_space_separator':
          value = formatWithDelimiters(cents, 0, ' ');
          break;
        case 'amount_with_apostrophe_separator':
          value = formatWithDelimiters(cents, 2, "'");
          break;
      }

      return formatString.replace(placeholderRegex, value);
    }

    function getBaseUnit(variant) {
      if (!variant) {
        return;
      }

      if (!variant.unit_price_measurement || !variant.unit_price_measurement.reference_value) {
        return;
      }

      return variant.unit_price_measurement.reference_value === 1
        ? variant.unit_price_measurement.reference_unit
        : variant.unit_price_measurement.reference_value +
            variant.unit_price_measurement.reference_unit;
    }

    return {
      formatMoney: formatMoney,
      getBaseUnit: getBaseUnit,
    };
  })();

  new FoxTheme.delayUntilInteraction(() => {
    document.body.removeAttribute('data-initializing');
  });

  FoxTheme.DOMready(FoxTheme.utils.setScrollbarWidth);
  // window.addEventListener('resize', FoxTheme.utils.throttle(FoxTheme.utils.setScrollbarWidth));

  const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
  FoxTheme.config.mqlMobile = mql.matches;
  mql.onchange = (event) => {
    if (event.matches) {
      FoxTheme.config.mqlMobile = true;
      document.dispatchEvent(new CustomEvent('matchMobile'));
    } else {
      FoxTheme.config.mqlMobile = false;
      document.dispatchEvent(new CustomEvent('unmatchMobile'));
    }
  };

  const mqlTablet = window.matchMedia(FoxTheme.config.mediaQueryTablet);
  FoxTheme.config.mqlTablet = mqlTablet.matches;
  mqlTablet.onchange = (event) => {
    if (event.matches) {
      FoxTheme.config.mqlTablet = true;
      document.dispatchEvent(new CustomEvent('matchTablet'));
    } else {
      FoxTheme.config.mqlTablet = false;
      document.dispatchEvent(new CustomEvent('unmatchTablet'));
    }
  };
})();

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (_e) {
  FoxTheme.focusVisiblePolyfill();
}

// eslint-disable-next-line no-unused-vars
function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage(
      '{"event":"command","func":"' + 'pauseVideo' + '","args":""}',
      '*'
    );
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

class PageTransition extends HTMLElement {
  // Static singleton instance tracker (prevents race conditions)
  static _instance = null;
  // Pending flag prevents race condition when multiple elements created simultaneously
  static _instancePending = false;

  // Observed attributes for attributeChangedCallback
  static get observedAttributes() {
    return ['navigation-timeout', 'image-timeout'];
  }

  constructor() {
    super();

    // Conditional logging setup (stored on instance for use in connectedCallback)
    this._isDev =
      window.location.hostname.includes('myshopify.com') ||
      window.location.hostname === 'localhost';
    // eslint-disable-next-line no-console
    this.log = this._isDev ? console.log.bind(console) : () => {};
    this.warn = this._isDev ? console.warn.bind(console) : () => {};

    // Singleton enforcement: Check both instance and pending flag
    // This prevents race condition when multiple elements created simultaneously
    if (PageTransition._instance || PageTransition._instancePending) {
      if (this._isDev) {
        console.error(
          '[PageTransition] Multiple <page-transition> elements detected! Only one is allowed. Removing duplicate.'
        );
      }
      this._isDuplicate = true;
      // Remove this duplicate element from DOM
      this.remove();
      return;
    }

    // Mark as not duplicate (will register in connectedCallback)
    this._isDuplicate = false;
    this._isConnected = false;

    // Store bound handlers for proper cleanup
    // Do all throwing code FIRST before reserving singleton slot
    this.boundHandleBeforeUnload = this.handleBeforeUnload.bind(this);
    this.boundHandleDOMContentLoaded = this.handleDOMContentLoaded.bind(this);
    this.boundHandlePageShow = this.handlePageShow.bind(this);
    this.boundHandlePopState = this.handlePopState.bind(this);
    this.boundHandleHashChange = this.handleHashChange.bind(this);
    this.imageLoadTimeout = null;
    this.programmaticNavigationTimeout = null;

    // Flag to track programmatic navigation (cart, menu dropdowns, etc.)
    this.isProgrammaticNavigation = false;

    // Guard flag to prevent double reveal
    this._pageRevealed = false;

    // MEDIUM FIX: Track image listeners for cleanup on disconnection
    // Stores {image, loadListener, errorListener} objects
    this._imageListeners = [];

    // Timeout values will be read from attributes in connectedCallback
    // (after element is in DOM and attributes are guaranteed to be set)
    this._navigationTimeout = 1000; // Default, will be updated in connectedCallback
    this._imageTimeout = 1200; // Default, will be updated in connectedCallback

    window.addEventListener('beforeunload', this.boundHandleBeforeUnload);
    window.addEventListener('DOMContentLoaded', this.boundHandleDOMContentLoaded);
    window.addEventListener('pageshow', this.boundHandlePageShow);
    window.addEventListener('popstate', this.boundHandlePopState);
    window.addEventListener('hashchange', this.boundHandleHashChange);

    // CRITICAL FIX: Reserve the singleton slot AFTER all throwing code succeeds
    // This prevents _instancePending from getting stuck if exception thrown above
    PageTransition._instancePending = true;

    // Safety timeout: Release pending flag if element never connects
    // Handles edge case where createElement is called but element never added to DOM
    this._pendingTimeout = setTimeout(() => {
      if (!this._isConnected && PageTransition._instancePending) {
        this.warn(
          '[PageTransition] Element created but never connected. Releasing singleton slot.'
        );
        PageTransition._instancePending = false;
      }
    }, 5000); // 5 second safety timeout
  }

  // Lifecycle: Set accessibility attributes when element is added to DOM
  connectedCallback() {
    // Guard: Skip if this is a duplicate that had early return in constructor
    // CRITICAL FIX: Actually remove duplicate from DOM (constructor remove() is no-op)
    if (this._isDuplicate) {
      if (window.FoxTheme?.debug) {
        this.warn(
          '[PageTransition] connectedCallback called on duplicate instance, removing from DOM'
        );
      }
      this.remove(); // Actually remove since constructor remove() was no-op
      return;
    }

    // CRITICAL: Check if another instance already registered (re-connection protection)
    // This handles case where element A is removed, element B registered, then A re-added
    if (PageTransition._instance && PageTransition._instance !== this) {
      if (this._isDev) {
        console.error(
          '[PageTransition] Another instance already connected. This element will be removed.'
        );
      }
      this._isDuplicate = true;

      // Clear safety timeout - DO NOT clear _instancePending (it may belong to another pending element)
      if (this._pendingTimeout) {
        clearTimeout(this._pendingTimeout);
        this._pendingTimeout = null;
      }

      this.remove();
      return;
    }

    // CRITICAL FIX: Check if we're re-connecting but someone else has reserved the slot
    // This prevents re-connecting element from stealing slot from a pending element
    // Re-connecting elements won't have _pendingTimeout (it was cleared on disconnection)
    if (PageTransition._instancePending && !this._pendingTimeout) {
      if (this._isDev) {
        console.error(
          '[PageTransition] Cannot re-connect: another instance has reserved the slot.'
        );
      }
      this._isDuplicate = true;
      this.remove();
      return;
    }

    // Clear safety timeout (element successfully connected)
    if (this._pendingTimeout) {
      clearTimeout(this._pendingTimeout);
      this._pendingTimeout = null;
    }

    // Read timeout attributes from DOM (now that element is connected)
    // This ensures attributes set after createElement but before appendChild are honored
    // Validate timeouts are positive (0, negative, NaN, null, undefined → default)
    // parseInt returns NaN for invalid inputs (null, undefined, "", "abc")
    const navTimeout = parseInt(this.getAttribute('navigation-timeout', 10), 10);
    this._navigationTimeout = navTimeout > 0 ? navTimeout : 1000;
    const imgTimeout = parseInt(this.getAttribute('image-timeout', 10), 10);
    this._imageTimeout = imgTimeout > 0 ? imgTimeout : 1200;

    // Register singleton now that element is actually in DOM
    PageTransition._instance = this;
    PageTransition._instancePending = false; // Clear pending flag, slot now filled
    this._isConnected = true;

    // Accessibility: Announce loading state to screen readers
    this.setAttribute('role', 'status');
    this.setAttribute('aria-live', 'polite');
    this.setAttribute('aria-label', 'Page loading indicator');

    if (window.FoxTheme?.debug) {
      this.log('[PageTransition] Connected and registered as singleton');
    }
  }

  // Lifecycle: Handle attribute changes after connection
  attributeChangedCallback(name, oldValue, newValue) {
    // Only process changes if element is connected and not a duplicate
    if (!this._isConnected || this._isDuplicate) {
      return;
    }

    if (name === 'navigation-timeout') {
      // Validate: must be positive integer (0, negative, NaN → default)
      const timeout = parseInt(newValue, 10);
      this._navigationTimeout = timeout > 0 ? timeout : 1000;
      if (window.FoxTheme?.debug) {
        this.log('[PageTransition] Navigation timeout updated to', this._navigationTimeout);
      }
    } else if (name === 'image-timeout') {
      // Validate: must be positive integer (0, negative, NaN → default)
      const timeout = parseInt(newValue, 10);
      this._imageTimeout = timeout > 0 ? timeout : 1200;
      if (window.FoxTheme?.debug) {
        this.log('[PageTransition] Image timeout updated to', this._imageTimeout);
      }
    }
  }

  handleBeforeUnload(event) {
    // CRITICAL FIX: Guard against disconnected elements manipulating DOM
    if (!this._isConnected || this._isDuplicate) {
      return;
    }

    if (window.FoxTheme?.debug) {
      this.log('[PageTransition] beforeunload fired, programmatic:', this.isProgrammaticNavigation);
    }

    // Respect other scripts that may have prevented default behavior
    if (event?.defaultPrevented) {
      if (window.FoxTheme?.debug) {
        this.log('[PageTransition] Skipping overlay (another script called preventDefault)');
      }
      return;
    }

    // Don't show loading overlay for programmatic navigation (cart, menus)
    if (this.isProgrammaticNavigation) {
      this.isProgrammaticNavigation = false;

      // Clear pending timeout since flag was consumed (prevents confusing warning)
      if (this.programmaticNavigationTimeout) {
        clearTimeout(this.programmaticNavigationTimeout);
        this.programmaticNavigationTimeout = null;
      }

      if (window.FoxTheme?.debug) {
        this.log('[PageTransition] Skipping overlay (programmatic navigation)');
      }
      return;
    }

    // Hide body immediately on navigation to prevent seeing old content
    document.body.classList.add('page-loading');
    if (window.FoxTheme?.debug) {
      this.log('[PageTransition] Loading overlay shown');
    }
  }

  /**
   * Marks the next page navigation as programmatic (e.g., cart, menu, quiz).
   * Prevents the loading overlay from showing for smooth in-page transitions.
   * Flag auto-resets after configured timeout (default 1000ms) if navigation doesn't occur.
   *
   * @public
   * @example
   * // Before programmatic navigation
   * const pageTransition = document.querySelector('page-transition');
   * if (pageTransition) {
   *   pageTransition.markNextNavigationAsProgrammatic();
   *   window.location.href = '/cart';
   * }
   */
  markNextNavigationAsProgrammatic() {
    // Prevent calls on disconnected or duplicate elements
    if (!this._isConnected || this._isDuplicate) {
      if (window.FoxTheme?.debug) {
        this.warn('[PageTransition] Cannot mark navigation on disconnected/duplicate element');
      }
      return;
    }

    this.isProgrammaticNavigation = true;

    if (window.FoxTheme?.debug) {
      this.log('[PageTransition] Programmatic navigation flagged');
    }

    // Clear any existing timeout to prevent race conditions
    if (this.programmaticNavigationTimeout) {
      clearTimeout(this.programmaticNavigationTimeout);
    }

    // Extended timeout for slow networks/servers (configurable via attribute)
    // Handles 3G connections, slow server responses, high latency scenarios
    // Resets flag if navigation doesn't occur
    this.programmaticNavigationTimeout = setTimeout(() => {
      if (window.FoxTheme?.debug && this.isProgrammaticNavigation) {
        this.warn('[PageTransition] Navigation flag expired (navigation may not have occurred)');
      }
      this.isProgrammaticNavigation = false;
      this.programmaticNavigationTimeout = null;
    }, this._navigationTimeout);
  }

  handleDOMContentLoaded() {
    // CRITICAL FIX: Guard against disconnected elements manipulating DOM
    if (!this._isConnected || this._isDuplicate) {
      return;
    }

    // Wait for all images to load before revealing the new page
    this.waitForImagesAndReveal();
    document.body.classList.add('page-loaded');
    document.dispatchEvent(new CustomEvent('page:loaded'));
  }

  waitForImagesAndReveal() {
    // MEDIUM FIX: Defensive guard - prevent disconnected elements from manipulating DOM
    // This method is internal but future-proofing against refactoring
    if (!this._isConnected || this._isDuplicate) {
      return;
    }

    // Reset reveal guard for new page load
    this._pageRevealed = false;

    // Clear any pending timeout
    if (this.imageLoadTimeout) {
      clearTimeout(this.imageLoadTimeout);
    }

    // MEDIUM FIX: Clean up previous image listeners before adding new ones
    this._cleanupImageListeners();

    const prioritySelectors = [
      'img[fetchpriority="high"]',
      'img[loading="eager"]',
      '.header__logo img',
      '.hero__media img',
      '[data-critical="true"]',
    ];
    const priorityImages = prioritySelectors
      .map((selector) => Array.from(document.querySelectorAll(selector)))
      .flat();
    const images = Array.from(new Set(priorityImages));

    if (images.length === 0) {
      // Nothing critical to wait for
      this.revealPage();
      return;
    }

    let imagesLoaded = 0;
    const checkAllLoaded = () => {
      if (imagesLoaded >= images.length) {
        this.revealPage();
      }
    };

    images.forEach((img) => {
      if (img.complete && img.naturalHeight !== 0) {
        imagesLoaded++;
        checkAllLoaded();
        return;
      }

      const onImageLoadOrError = () => {
        imagesLoaded++;
        checkAllLoaded();
      };

      // MEDIUM FIX: Store listener references for cleanup on disconnection
      // Note: { once: true } auto-removes listeners, but we need manual cleanup if element disconnects first
      img.addEventListener('load', onImageLoadOrError, { once: true });
      img.addEventListener('error', onImageLoadOrError, { once: true });
      this._imageListeners.push({ image: img, listener: onImageLoadOrError });
    });

    // Ensure we don't block longer than configured timeout (default 1.2s)
    this.imageLoadTimeout = setTimeout(() => {
      this.revealPage();
    }, this._imageTimeout);
  }

  // MEDIUM FIX: Clean up image listeners to prevent memory leaks
  _cleanupImageListeners() {
    this._imageListeners.forEach(({ image, listener }) => {
      // Remove both load and error listeners (same function reference)
      image.removeEventListener('load', listener);
      image.removeEventListener('error', listener);
    });
    this._imageListeners = [];
  }

  revealPage() {
    // MEDIUM FIX: Defensive guard - prevent disconnected elements from manipulating DOM
    // Protects against future refactoring and race conditions
    if (!this._isConnected || this._isDuplicate) {
      return;
    }

    // Guard: Prevent multiple calls from both image load and timeout
    if (this._pageRevealed) {
      return;
    }
    this._pageRevealed = true;

    if (this.imageLoadTimeout) {
      clearTimeout(this.imageLoadTimeout);
      this.imageLoadTimeout = null;
    }

    // Remove page-loading class to trigger CSS visibility change
    document.body.classList.remove('page-loading');

    // Graceful degradation if Motion library unavailable
    if (window.FoxTheme?.Motion?.animate) {
      FoxTheme.Motion.animate(this, { visibility: 'hidden', opacity: 0 }, { duration: 1 });
    } else {
      // Fallback: Hide immediately with CSS
      this.style.visibility = 'hidden';
      this.style.opacity = '0';
      if (window.FoxTheme?.debug) {
        this.warn('[PageTransition] Motion library unavailable, using CSS fallback');
      }
    }
  }

  handlePageShow(event) {
    // CRITICAL FIX: Guard against disconnected elements manipulating DOM
    if (!this._isConnected || this._isDuplicate) {
      return;
    }

    if (event.persisted) {
      document.body.classList.remove('page-loading');
    }
  }

  handlePopState() {
    // CRITICAL FIX: Guard against disconnected elements manipulating state
    if (!this._isConnected || this._isDuplicate) {
      return;
    }

    // Clear any pending programmatic timeout to prevent race conditions
    if (this.programmaticNavigationTimeout) {
      clearTimeout(this.programmaticNavigationTimeout);
      this.programmaticNavigationTimeout = null;
    }

    // Only set if not already marked (prevents hash+popstate conflict)
    if (!this.isProgrammaticNavigation) {
      this.isProgrammaticNavigation = true;
      if (window.FoxTheme?.debug) {
        this.log('[PageTransition] popstate: Browser back/forward navigation detected');
      }

      // CRITICAL FIX: Start timeout to reset flag (prevents stuck flag forever)
      // Without this, after any back/forward navigation, the flag stays true permanently
      // and all future programmatic navigation breaks (cart, menus show overlay)
      this.programmaticNavigationTimeout = setTimeout(() => {
        if (window.FoxTheme?.debug && this.isProgrammaticNavigation) {
          this.warn('[PageTransition] popstate flag expired after timeout');
        }
        this.isProgrammaticNavigation = false;
        this.programmaticNavigationTimeout = null;
      }, this._navigationTimeout);
    }
  }

  handleHashChange() {
    // CRITICAL FIX: Guard against disconnected elements manipulating state
    if (!this._isConnected || this._isDuplicate) {
      return;
    }

    // Clear any pending programmatic timeout to prevent race conditions
    if (this.programmaticNavigationTimeout) {
      clearTimeout(this.programmaticNavigationTimeout);
      this.programmaticNavigationTimeout = null;
    }

    // Only set if not already marked (prevents hash+popstate conflict)
    if (!this.isProgrammaticNavigation) {
      this.isProgrammaticNavigation = true;
      if (window.FoxTheme?.debug) {
        this.log('[PageTransition] hashchange: Hash-only navigation detected');
      }

      // CRITICAL FIX: Start timeout to reset flag (prevents stuck flag forever)
      // Without this, after any hash navigation, the flag stays true permanently
      // and all future programmatic navigation breaks (cart, menus show overlay)
      this.programmaticNavigationTimeout = setTimeout(() => {
        if (window.FoxTheme?.debug && this.isProgrammaticNavigation) {
          this.warn('[PageTransition] hashchange flag expired after timeout');
        }
        this.isProgrammaticNavigation = false;
        this.programmaticNavigationTimeout = null;
      }, this._navigationTimeout);
    }
  }

  // Cleanup: Remove all global event listeners when element is removed from DOM
  disconnectedCallback() {
    // CRITICAL FIX: Always clear timeouts for ALL elements (including duplicates)
    // Prevents timeout leaks for rejected elements
    if (this._pendingTimeout) {
      clearTimeout(this._pendingTimeout);
      this._pendingTimeout = null;
    }
    if (this.imageLoadTimeout) {
      clearTimeout(this.imageLoadTimeout);
      this.imageLoadTimeout = null;
    }
    if (this.programmaticNavigationTimeout) {
      clearTimeout(this.programmaticNavigationTimeout);
      this.programmaticNavigationTimeout = null;
    }

    // MEDIUM FIX: Clean up image listeners to prevent memory leaks
    // If element disconnects before images load, listeners remain attached
    this._cleanupImageListeners();

    // CRITICAL FIX: Always remove event listeners (even for duplicates)
    // Prevents memory leak for elements rejected in connectedCallback
    // Elements rejected in connectedCallback have listeners added but were marked as duplicates
    // Without this, 5 global event listeners leak per rejected element
    if (this.boundHandleBeforeUnload) {
      window.removeEventListener('beforeunload', this.boundHandleBeforeUnload);
      window.removeEventListener('DOMContentLoaded', this.boundHandleDOMContentLoaded);
      window.removeEventListener('pageshow', this.boundHandlePageShow);
      window.removeEventListener('popstate', this.boundHandlePopState);
      window.removeEventListener('hashchange', this.boundHandleHashChange);
    }

    // Early return for duplicates (before singleton cleanup)
    // Duplicates should not clear singleton state
    if (this._isDuplicate) {
      return;
    }

    // Only clear singleton state for non-duplicates (the active instance)
    this._isConnected = false;

    if (PageTransition._instance === this) {
      PageTransition._instance = null;
    }
  }
}

// Guard against double registration (e.g., if script loads twice)
if (!customElements.get('page-transition')) {
  customElements.define('page-transition', PageTransition);
}

const clearModalComponentCount = new WeakMap();
class ModalComponent extends HTMLElement {
  constructor() {
    super();
    this.events = {
      handleAfterHide: 'modal:handleAfterHide',
      handleAfterShow: 'modal:handleAfterShow',
    };

    this.classes = {
      show: 'modal-show',
      showing: 'modal-showing',
    };

    // Timer IDs for cleanup
    this.afterHideTimerId = null;
    this.showTransitionTimerId = null;
  }

  static get observedAttributes() {
    return ['id', 'open'];
  }

  get isLockingNeeded() {
    return false;
  }

  get requiresBodyAppended() {
    return false;
  }

  get controls() {
    return Array.from(document.querySelectorAll(`[aria-controls="${this.id}"]`));
  }

  get designMode() {
    return this.hasAttribute('shopify-design-mode');
  }

  get open() {
    return this.hasAttribute('open');
  }

  get overlay() {
    // Check if the _overlay property is already set
    if (!this._overlay) {
      // If not set, find the element and cache it
      this._overlay = this.querySelector('.fixed-overlay');
    }

    // Return the cached element
    return this._overlay;
  }

  get focusElement() {
    return this.querySelector('button');
  }
  connectedCallback() {
    // Initialize the AbortController
    this.abortController = new AbortController();

    // Add click event listeners to all controls
    this.controls.forEach((button) => {
      button.addEventListener('click', this.onButtonClick.bind(this), {
        signal: this.abortController.signal,
      });
    });

    // Add keyup event listener to document for handling the Escape key
    document.addEventListener(
      'keyup',
      (event) => {
        if (event.code === 'Escape') {
          this.hide();
        }
      },
      { signal: this.abortController.signal }
    );

    // Additional setup for Shopify design mode
    if (this.designMode && Shopify.designMode) {
      const section = this.closest('.shopify-section');
      section.addEventListener(
        'shopify:section:select',
        (event) => {
          this.show(null, !event.detail.load);
        },
        {
          signal: this.abortController.signal,
        }
      );
      section.addEventListener(
        'shopify:section:deselect',
        () => {
          this.hide();
        },
        {
          signal: this.abortController.signal,
        }
      );
    }
  }

  disconnectedCallback() {
    this.abortController.abort();

    // Clean up timers
    if (this.afterHideTimerId) {
      clearTimeout(this.afterHideTimerId);
      this.afterHideTimerId = null;
    }
    if (this.showTransitionTimerId) {
      clearTimeout(this.showTransitionTimerId);
      this.showTransitionTimerId = null;
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    switch (name) {
      case 'open':
        this.controls.forEach((button) =>
          button.setAttribute('aria-expanded', newValue === null ? 'false' : 'true')
        );

        if (oldValue === null && (newValue === '' || newValue === 'immediate')) {
          this.hidden = false;
          this.removeAttribute('inert');

          this.parentElementBeforeAppend = null;
          if (this.requiresBodyAppended && this.parentElement !== document.body) {
            this.parentElementBeforeAppend = this.parentElement;
            document.body.append(this);
          }
          const handleShowTransitionPromise =
            this.handleShowTransition(newValue !== 'immediate') || Promise.resolve();
          handleShowTransitionPromise.then(() => {
            this.handleAfterShow();
            this.dispatchEvent(new CustomEvent(this.events.handleAfterShow, { bubbles: true }));
          });
        } else if (oldValue !== null && newValue === null) {
          this.setAttribute('inert', '');

          const handleHideTransitionPromise = this.handleHideTransition() || Promise.resolve();
          handleHideTransitionPromise.then(() => {
            this.handleAfterHide();

            if (!this.hasAttribute('inert')) return;

            if (this.parentElement === document.body && this.parentElementBeforeAppend) {
              this.parentElementBeforeAppend.appendChild(this);
              this.parentElementBeforeAppend = null;
            }
            this.dispatchEvent(new CustomEvent(this.events.handleAfterHide, { bubbles: true }));

            this.hidden = true;
          });
        }
        this.dispatchEvent(new CustomEvent('toggle', { bubbles: true }));

        break;
    }
  }

  onButtonClick(event) {
    event.preventDefault();
    if (this.open) {
      this.hide();
    } else {
      this.show(event.currentTarget);
    }
  }

  hide() {
    if (this.open) {
      this.removeAttribute('open');
      return FoxTheme.utils.waitForEvent(this, this.events.handleAfterHide);
    }
  }

  show(activeElement = null, animate = true) {
    if (!this.shouleBeShow()) {
      return;
    }

    if (!this.open) {
      this.prepareToShow();
      this.activeElement = activeElement;
      this.setAttribute('open', animate ? '' : 'immediate');

      if (this.isLockingNeeded) {
        document.body.classList.add(this.classes.showing);
      }

      return FoxTheme.utils.waitForEvent(this, this.events.handleAfterShow);
    }
  }

  handleAfterHide() {
    // CRITICAL FIX: Immediately clean up modal classes to restore scrolling
    // Do NOT defer with setTimeout as it can be cancelled, causing stuck scroll lock
    if (this.isLockingNeeded) {
      // Decrement the lock layer count for the ModalElement
      const curentModalCount = clearModalComponentCount.get(ModalComponent) - 1;
      clearModalComponentCount.set(ModalComponent, curentModalCount);

      // Remove modal-showing class immediately to ensure scrolling is restored
      document.body.classList.remove(this.classes.showing);

      // Toggle the 'open' class on the body based on the current lock count
      document.body.classList.toggle(this.classes.show, curentModalCount > 0);
    }

    // Defer only non-critical cleanup operations
    // Clear existing timer before setting new one
    if (this.afterHideTimerId) {
      clearTimeout(this.afterHideTimerId);
    }

    this.afterHideTimerId = setTimeout(() => {
      // Remove trap focus from the active element
      FoxTheme.a11y.removeTrapFocus(this.activeElement);
    }, 0);
  }

  handleAfterShow() {
    // Check if locking is needed
    if (this.isLockingNeeded) {
      // Increment the lock layer count for the ModalElement
      const currentLockCount = clearModalComponentCount.get(ModalComponent) + 1;
      clearModalComponentCount.set(ModalComponent, currentLockCount);

      // Manage class changes on the document body
      document.body.classList.remove(this.classes.showing);
      document.body.classList.add(this.classes.show);
    }

    // Defer focus trap to a separate frame so the drawer appears immediately
    // getFocusableElements() can be expensive when scanning the entire DOM
    // We want the visual appearance (CSS transition) to happen first,
    // then set up keyboard navigation after the browser repaints
    requestAnimationFrame(() => {
      FoxTheme.a11y.trapFocus(this, this.focusElement);
    });
  }

  shouleBeShow() {
    return true;
  }

  prepareToShow() {}

  handleShowTransition() {
    // Clear existing timer before setting new one
    if (this.showTransitionTimerId) {
      clearTimeout(this.showTransitionTimerId);
    }

    // Start a timeout to set an attribute
    this.showTransitionTimerId = setTimeout(() => {
      this.setAttribute('active', '');
    }, 75);

    // Return a promise that resolves when the transition ends
    return new Promise((resolve) => {
      this.overlay.addEventListener('transitionend', resolve, { once: true });
    });
  }

  handleHideTransition() {
    // Immediately remove the 'active' attribute
    this.removeAttribute('active');

    // Return a promise that resolves when the transition ends
    return new Promise((resolve) => {
      this.overlay.addEventListener('transitionend', resolve, { once: true });
    });
  }
}
customElements.define('modal-component', ModalComponent);
clearModalComponentCount.set(ModalComponent, 0);

// Safety cleanup function to fix stuck modal/drawer classes
function cleanupStuckModal() {
  const openModals = document.querySelectorAll(
    '[open]:is(modal-component, basic-modal, drawer-component, cart-drawer, search-drawer, menu-drawer)'
  );
  const hasModalShowClass = document.body.classList.contains('modal-show');
  const hasModalShowingClass = document.body.classList.contains('modal-showing');

  // If modal classes are present but no modals are open, remove them
  if ((hasModalShowClass || hasModalShowingClass) && openModals.length === 0) {
    document.body.classList.remove('modal-show', 'modal-showing');
    clearModalComponentCount.set(ModalComponent, 0);
  }
  // If no modal classes but modals are open, add the correct class
  else if (!hasModalShowClass && !hasModalShowingClass && openModals.length > 0) {
    document.body.classList.add('modal-show');
    clearModalComponentCount.set(ModalComponent, openModals.length);
  }
  // Sync counter with actual open modals
  else if ((hasModalShowClass || hasModalShowingClass) && openModals.length > 0) {
    clearModalComponentCount.set(ModalComponent, openModals.length);
  }
}

// Run cleanup on page visibility change (when user returns to tab)
document.addEventListener('visibilitychange', () => {
  if (!document.hidden) {
    cleanupStuckModal();
  }
});

// Run cleanup periodically (every 10 seconds) to catch any stuck states
setInterval(cleanupStuckModal, 10000);

class BasicModal extends ModalComponent {
  constructor() {
    super();
  }

  get isLockingNeeded() {
    return true;
  }

  get requiresBodyAppended() {
    return true;
  }
}
customElements.define('basic-modal', BasicModal);

class DrawerComponent extends ModalComponent {
  constructor() {
    super();
    this.events = {
      handleAfterHide: 'drawer:handleAfterHide',
      handleAfterShow: 'drawer:handleAfterShow',
    };
  }
  get isLockingNeeded() {
    return true;
  }
  get requiresBodyAppended() {
    return true;
  }
  prepareToShow() {
    // Close all other open drawers before showing this one
    const openDrawers = document.querySelectorAll(
      'drawer-component[open], search-drawer[open], menu-drawer[open]'
    );
    openDrawers.forEach((drawer) => {
      if (drawer !== this && drawer.open) {
        drawer.hide();
      }
    });
    super.prepareToShow();
  }
}
customElements.define('drawer-component', DrawerComponent);

class AccordionDetails extends HTMLDetailsElement {
  constructor() {
    super();

    // Store bound handlers for proper cleanup
    this.boundToggleAccordion = this.toggleAccordion.bind(this);
    this.boundHandleBlockSelect = this.handleBlockSelect.bind(this);
    this.boundHandleBlockDeselect = this.handleBlockDeselect.bind(this);
    this.lastEventTime = 0;

    this.initAccordion();
  }

  initAccordion() {
    this.isOpen = this.hasAttribute('open');
    this.summaryElement = this.querySelector('summary');
    this.contentElement = this.querySelector('summary + *');
    this.setAttribute('aria-expanded', this.isOpen ? 'true' : 'false');

    // Add pointer events for better mobile support
    if (this.summaryElement) {
      this.summaryElement.addEventListener('pointerdown', this.boundToggleAccordion);
      this.summaryElement.addEventListener('click', this.boundToggleAccordion);
    }

    if (Shopify.designMode) {
      this.designModeEventSetup();
    }
  }

  static get observedAttributes() {
    return ['open'];
  }

  get open() {
    return this.isOpen;
  }

  set open(value) {
    if (value !== this.isOpen) {
      this.isOpen = value;

      if (this.isConnected) {
        this.animateAccordion(value);
      } else {
        if (value) {
          this.setAttribute('open', '');
        } else {
          this.removeAttribute('open');
        }
      }

      this.setAttribute('aria-expanded', value ? 'true' : 'false');
      this.setAttribute('aria-controls', this.contentElement.id);

      this.handleAfterToggle();
    }
  }

  handleAfterToggle() {}

  toggleAccordion(event) {
    // Prevent double-firing when both pointer and click events trigger
    const now = Date.now();
    if (now - this.lastEventTime < 300) {
      event.preventDefault();
      return;
    }
    this.lastEventTime = now;

    event.preventDefault();
    this.open = !this.open;
  }

  close() {
    this.isOpen = false;
    this.animateAccordion(false);
  }

  async animateAccordion(isOpen) {
    // Batch all DOM reads before any writes to prevent layout thrashing
    const summaryHeight = this.summaryElement.clientHeight;
    const totalScrollHeight = this.scrollHeight;
    const currentHeight = this.clientHeight;

    // Now perform all writes
    this.style.overflow = 'hidden';

    if (isOpen) {
      this.setAttribute('open', '');

      await FoxTheme.Motion.timeline([
        [
          this,
          { height: [`${summaryHeight + 1}px`, `${totalScrollHeight + 1}px`] },
          { duration: 0.25, easing: 'ease' },
        ],
        [
          this.contentElement,
          { opacity: [0, 1], transform: ['translateY(10px)', 'translateY(0)'] },
          { duration: 0.15, at: '<' },
        ],
      ]).finished;
    } else {
      await FoxTheme.Motion.timeline([
        [this.contentElement, { opacity: 0 }, { duration: 0.15 }],
        [
          this,
          { height: [`${currentHeight + 1}px`, `${summaryHeight + 1}px`] },
          { duration: 0.25, at: '<', easing: 'ease' },
        ],
      ]).finished;

      this.removeAttribute('open');
    }

    this.style.height = 'auto';
    this.style.overflow = 'visible';
  }

  handleBlockSelect() {
    this.open = true;
  }

  handleBlockDeselect() {
    this.open = false;
  }

  designModeEventSetup() {
    this.addEventListener('shopify:block:select', this.boundHandleBlockSelect);
    this.addEventListener('shopify:block:deselect', this.boundHandleBlockDeselect);
  }

  // Cleanup: Remove all event listeners when element is removed from DOM
  disconnectedCallback() {
    if (this.summaryElement) {
      this.summaryElement.removeEventListener('pointerdown', this.boundToggleAccordion);
      this.summaryElement.removeEventListener('click', this.boundToggleAccordion);
    }
    if (Shopify.designMode) {
      this.removeEventListener('shopify:block:select', this.boundHandleBlockSelect);
      this.removeEventListener('shopify:block:deselect', this.boundHandleBlockDeselect);
    }
  }

  attributeChangedCallback(name, _oldValue, _newValue) {
    if (name === 'open') {
      this.setAttribute('aria-controls', this.contentElement.id);
    }
  }
}
customElements.define('accordion-details', AccordionDetails, { extends: 'details' });

class AccordionGroup extends AccordionDetails {
  constructor() {
    super();
  }

  handleAfterToggle() {
    if (this.isOpen) {
      const parent = this.closest('.accordion-parent') || document;
      const siblingsAccordions = parent.querySelectorAll('details[is="accordion-group"]');

      siblingsAccordions.forEach((details) => {
        if (details !== this) {
          details.open = false;
        }
      });
    }
  }
}
customElements.define('accordion-group', AccordionGroup, { extends: 'details' });

class ProgressBar extends HTMLElement {
  constructor() {
    super();

    if (this.hasAttribute('style')) return;

    if (this.dataset.from) {
      this.initProgress();
    }

    FoxTheme.Motion.inView(this, this.init.bind(this));
  }

  init() {
    this.setPercentage(this.dataset.value, this.dataset.max);
  }

  initProgress() {
    this.setPercentage(this.dataset.from, this.dataset.max);
  }

  setPercentage(val1, val2) {
    this.style.setProperty('--percent', `${(parseInt(val1, 10) / parseInt(val2, 10)) * 100}%`);
  }
}
customElements.define('progress-bar', ProgressBar);

class CartCount extends HTMLElement {
  constructor() {
    super();
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
      this.onCartUpdate.bind(this)
    );
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  get itemCount() {
    return parseInt(this.innerText, 10);
  }

  onCartUpdate(event) {
    if (event.cart.errors) return;

    const itemCount = parseInt(event.cart.item_count ?? 0, 10);
    if (isNaN(itemCount)) return;

    if (itemCount > 99) {
      // Create span safely with DOM methods
      const span = document.createElement('span');
      span.className = 'text-sm';
      span.textContent = '99+';
      this.replaceChildren(span);
      this.classList.add('cart-count--small-medium');
    } else {
      this.innerText = itemCount;
      this.classList.remove('cart-count--small-medium');
    }
    this.hidden = itemCount === 0;

    const method = itemCount === 0 ? 'remove' : 'add';
    document.documentElement.classList[method]('cart-has-items');
  }
}
customElements.define('cart-count', CartCount);

class QuantityInput extends HTMLInputElement {
  constructor() {
    super();

    // Store bound handlers for proper cleanup
    this.boundOnValueInput = this.onValueInput.bind(this);
    this.boundOnValueChanged = this.onValueChanged.bind(this);
    this.boundOnKeyDown = this.onKeyDown.bind(this);
    this.boundOnFocus = this.select.bind(this);

    this.addEventListener('input', this.boundOnValueInput);
    this.addEventListener('change', this.boundOnValueChanged);
    this.addEventListener('keydown', this.boundOnKeyDown);
    this.addEventListener('focus', this.boundOnFocus);
  }

  get quantity() {
    return parseInt(this.value, 10);
  }

  set quantity(quantity) {
    const isNumeric =
      (typeof quantity === 'number' || (typeof quantity === 'string' && quantity.trim() !== '')) &&
      !isNaN(quantity);
    if (quantity === '') return;

    if (!isNumeric || quantity < 0) {
      quantity = parseInt(quantity, 10) || 1;
    }

    this.value = Math.max(
      this.min || 1,
      Math.min(quantity, this.max || Number.MAX_VALUE)
    ).toString();
    this.size = Math.max(this.value.length + 1, 2);
  }

  onValueInput() {
    // Enterprise Fix: Prevent empty string during input
    if (this.value.trim() === '') {
      // Don't update quantity during empty input to prevent form submission with empty value
      return;
    }
    this.quantity = this.value;
  }

  onValueChanged() {
    // Enterprise Fix: Handle empty or whitespace-only values
    if (this.value === '' || this.value.trim() === '') {
      this.quantity = 1;
    }
  }

  onKeyDown(event) {
    event.stopPropagation();

    if (event.key === 'ArrowUp') {
      this.quantity++;
    } else if (event.key === 'ArrowDown') {
      this.quantity--;
    }
  }

  // Cleanup: Remove all event listeners when element is removed from DOM
  disconnectedCallback() {
    this.removeEventListener('input', this.boundOnValueInput);
    this.removeEventListener('change', this.boundOnValueChanged);
    this.removeEventListener('keydown', this.boundOnKeyDown);
    this.removeEventListener('focus', this.boundOnFocus);
  }
}
customElements.define('quantity-input', QuantityInput, { extends: 'input' });

class QuantitySelector extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.abortController = new AbortController();

    this.input = this.querySelector('input');
    this.buttons = Array.from(this.querySelectorAll('button'));
    this.changeEvent = new Event('change', { bubbles: true });

    this.buttons.forEach(
      (button) => button.addEventListener('click', this.onButtonClick.bind(this)),
      {
        signal: this.abortController.signal,
      }
    );
  }

  disconnectedCallback() {
    this.abortController.abort();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.currentTarget.name === 'plus') {
      this.input.quantity = this.input.quantity + 1;
    } else {
      this.input.quantity = this.input.quantity - 1;
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
  }
}
customElements.define('quantity-selector', QuantitySelector);

const onYouTubeApiLoaded = new Promise((resolve) => {
  window.onYouTubeIframeAPIReady = () => resolve();
});
class VideoElement extends HTMLElement {
  constructor() {
    super();

    // Store bound handlers for proper cleanup
    this.boundHandlePosterClick = this.handlePosterClick.bind(this);
    this.boundHandlePlay = this.handlePlay.bind(this);
    this.boundHandlePause = this.handlePause.bind(this);

    if (this.posterElement) {
      this.posterElement.addEventListener('click', this.boundHandlePosterClick);
    }

    if (this.autoplay) {
      FoxTheme.Motion.inView(this, () => {
        if (!this.paused) {
          this.play();
        }

        return () => {
          this.pause();
        };
      });
    }
  }

  get posterElement() {
    return this.querySelector('[id^="DeferredPoster-"]');
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls')
      ? document.getElementById(this.getAttribute('aria-controls'))
      : null;
  }

  get autoplay() {
    return this.hasAttribute('autoplay');
  }

  get playing() {
    return this.hasAttribute('playing');
  }

  get player() {
    return (this.playerProxy =
      this.playerProxy ||
      new Proxy(this.initializePlayer(), {
        get: (target, prop) => {
          return async () => {
            // eslint-disable-next-line require-atomic-updates
            target = await target;
            this.handlePlayerAction(target, prop);
          };
        },
      }));
  }

  static get observedAttributes() {
    return ['playing'];
  }

  handlePosterClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.playing) {
      this.paused = true;
      this.pause();
    } else {
      this.paused = false;
      this.play();
    }
  }

  play() {
    if (!this.playing) {
      this.player.play();
    }
  }

  pause() {
    if (this.playing) {
      this.player.pause();
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'playing') {
      if (oldValue === null && newValue === '') {
        return this.dispatchEvent(new CustomEvent('video:play', { bubbles: true }));
      }

      if (newValue === null) {
        return this.dispatchEvent(new CustomEvent('video:pause', { bubbles: true }));
      }
    }
  }

  async initializePlayer() {
    if (this.hasAttribute('source')) {
      this.setAttribute('loaded', '');
      this.closest('.media')?.classList.remove('loading');

      const templateElement = this.querySelector('template');
      if (templateElement) {
        templateElement.replaceWith(templateElement.content.firstElementChild.cloneNode(true));
      }
      const muteVideo =
        this.hasAttribute('autoplay') ||
        window.matchMedia('screen and (max-width: 1023px)').matches;
      const script = document.createElement('script');
      script.type = 'text/javascript';
      if (this.getAttribute('source') === 'youtube') {
        if (!window.YT || !window.YT.Player) {
          script.src = 'https://www.youtube.com/iframe_api';
          document.head.appendChild(script);
          await new Promise((resolve2) => {
            script.onload = resolve2;
          });
        }
        await onYouTubeApiLoaded;
        return new Promise((resolve) => {
          const player = new YT.Player(this.querySelector('iframe'), {
            events: {
              onReady: () => {
                if (muteVideo) {
                  player.mute();
                }
                resolve(player);
              },
              onStateChange: (event) => {
                if (event.data === YT.PlayerState.PLAYING) {
                  this.setAttribute('playing', '');
                } else if (
                  event.data === YT.PlayerState.ENDED ||
                  event.data === YT.PlayerState.PAUSED
                ) {
                  this.removeAttribute('playing');
                }
              },
            },
          });
        });
      }
      if (this.getAttribute('source') === 'vimeo') {
        if (!window.Vimeo || !window.Vimeo.Player) {
          script.src = 'https://player.vimeo.com/api/player.js';
          document.head.appendChild(script);
          await new Promise((resolve2) => {
            script.onload = resolve2;
          });
        }
        const player = new Vimeo.Player(this.querySelector('iframe'));
        if (muteVideo) {
          player.setMuted(true);
        }
        player.on('play', () => {
          this.setAttribute('playing', '');
        });
        player.on('pause', () => this.removeAttribute('playing'));
        player.on('ended', () => this.removeAttribute('playing'));
        return player;
      }
    } else {
      this.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));
      this.setAttribute('loaded', '');
      this.closest('.media')?.classList.remove('loading');

      const player = this.querySelector('video');
      player.addEventListener('play', this.boundHandlePlay);
      player.addEventListener('pause', this.boundHandlePause);

      // Store player reference for cleanup
      this.videoPlayer = player;

      return player;
    }
  }

  handlePlay() {
    this.setAttribute('playing', '');
    this.removeAttribute('suspended');
  }

  handlePause() {
    if (this.videoPlayer && !this.videoPlayer.seeking && this.videoPlayer.paused) {
      this.removeAttribute('playing');
    }
  }

  handlePlayerAction(target, prop) {
    if (this.getAttribute('source') === 'youtube') {
      prop === 'play' ? target.playVideo() : target.pauseVideo();
    } else {
      if (prop === 'play' && !this.hasAttribute('source')) {
        target.play().catch((error) => {
          if (error.name === 'NotAllowedError') {
            this.setAttribute('suspended', '');
            target.controls = true;
            const replacementImageSrc = target.previousElementSibling?.currentSrc;
            if (replacementImageSrc) {
              target.poster = replacementImageSrc;
            }
          }
        });
      } else {
        target[prop]();
      }
    }
  }

  // Cleanup: Remove all event listeners when element is removed from DOM
  disconnectedCallback() {
    if (this.posterElement) {
      this.posterElement.removeEventListener('click', this.boundHandlePosterClick);
    }
    if (this.videoPlayer) {
      this.videoPlayer.removeEventListener('play', this.boundHandlePlay);
      this.videoPlayer.removeEventListener('pause', this.boundHandlePause);
    }
  }
}
customElements.define('video-element', VideoElement);

class LocalizationForm extends HTMLElement {
  constructor() {
    super();

    // Store bound handlers for proper cleanup
    this.boundOpenSelector = this.openSelector.bind(this);
    this.boundCloseSelector = this.closeSelector.bind(this);
    this.boundOnContainerKeyUp = this.onContainerKeyUp.bind(this);
    this.boundOnItemClick = this.onItemClick.bind(this);

    this.elements = {
      input: this.querySelector('input[name="locale_code"], input[name="country_code"]'),
      button: this.querySelector('button'),
      panel: this.querySelector('.disclosure-list'),
    };

    this.elements.panel.removeAttribute('hidden');
    this.elements.button.addEventListener('click', this.boundOpenSelector);
    this.elements.button.addEventListener('focusout', this.boundCloseSelector);
    this.addEventListener('keyup', this.boundOnContainerKeyUp);

    // Store link elements for cleanup
    this.linkElements = this.querySelectorAll('a');
    this.linkElements.forEach((item) => item.addEventListener('click', this.boundOnItemClick));

    this.handleDropdownPos();
  }

  handleDropdownPos() {
    const offsetButton = this.elements.button.getBoundingClientRect().right;
    if (window.innerWidth - offsetButton < 220) {
      this.elements.button.nextElementSibling &&
        this.elements.button.nextElementSibling.classList.add('disclosure-list__right');
    }
  }

  hidePanel() {
    this.elements.button.setAttribute('aria-expanded', 'false');
    this.removeAttribute('open');
  }

  onContainerKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    this.hidePanel();
    this.elements.button.focus();
  }

  onItemClick(event) {
    event.preventDefault();
    const form = this.querySelector('form');
    this.elements.input.value = event.currentTarget.dataset.value;
    if (form) form.submit();
  }

  openSelector() {
    this.elements.button.focus();
    this.toggleAttribute('open');
    this.elements.button.setAttribute(
      'aria-expanded',
      (this.elements.button.getAttribute('aria-expanded') === 'false').toString()
    );
  }

  closeSelector(event) {
    const shouldClose =
      (event.relatedTarget && event.relatedTarget.nodeName === 'BUTTON') ||
      (event.relatedTarget && !event.relatedTarget.classList.contains('disclosure-list__option'));
    if (event.relatedTarget === null || shouldClose) {
      this.hidePanel(shouldClose);
    }
  }

  // Cleanup: Remove all event listeners when element is removed from DOM
  disconnectedCallback() {
    if (this.elements.button) {
      this.elements.button.removeEventListener('click', this.boundOpenSelector);
      this.elements.button.removeEventListener('focusout', this.boundCloseSelector);
    }
    this.removeEventListener('keyup', this.boundOnContainerKeyUp);
    this.linkElements.forEach((item) => item.removeEventListener('click', this.boundOnItemClick));
  }
}
customElements.define('localization-form', LocalizationForm);

class GridList extends HTMLElement {
  constructor() {
    super();

    if (FoxTheme.config.motionReduced || this.hasAttribute('motion-reduced')) return;

    this.hideGridItems();
    FoxTheme.Motion.inView(this, this.showGridItems.bind(this), { margin: '0px 0px -50px 0px' });
  }

  get gridItems() {
    return this.querySelectorAll('.f-column');
  }

  get visibleGridItems() {
    return this.querySelectorAll('.f-column:not([style])');
  }

  hideGridItems() {
    FoxTheme.Motion.animate(
      this.gridItems,
      { transform: 'translateY(3.5rem)', opacity: 0.01, visibility: 'hidden' },
      { duration: 0 }
    );
  }

  showGridItems() {
    FoxTheme.Motion.animate(
      this.gridItems,
      {
        transform: ['translateY(3.5rem)', 'translateY(0)'],
        opacity: [0.01, 1],
        visibility: ['hidden', 'visible'],
      },
      {
        duration: 0.5,
        delay: FoxTheme.config.motionReduced ? 0 : FoxTheme.Motion.stagger(0.1),
        easing: [0, 0, 0.3, 1],
      }
    );
  }

  reShowVisibleGridItems() {
    FoxTheme.Motion.animate(
      this.visibleGridItems,
      {
        transform: ['translateY(3.5rem)', 'translateY(0)'],
        opacity: [0.01, 1],
        visibility: ['hidden', 'visible'],
      },
      {
        duration: 0.5,
        delay: FoxTheme.config.motionReduced ? 0 : FoxTheme.Motion.stagger(0.1),
        easing: [0, 0, 0.3, 1],
      }
    );
  }
}
customElements.define('grid-list', GridList);

class AnnouncementBar extends HTMLElement {
  constructor() {
    super();

    // Store bound handler for proper cleanup
    this.boundHandleBlockSelect = this.handleBlockSelect.bind(this);

    this.announcementItemsWrapper = this.querySelector('.swiper-wrapper');

    FoxTheme.Motion.inView(this, this.initializeCarousel.bind(this), {
      margin: '200px 0px 200px 0px',
    });
  }

  getNextSlideButton() {
    return this.querySelector('.swiper-button-next');
  }

  getPrevSlideButton() {
    return this.querySelector('.swiper-button-prev');
  }

  get getSlideItems() {
    return (this._cachedSlideItems =
      this._cachedSlideItems || Array.from(this.announcementItemsWrapper.children));
  }

  isAutoplayEnabled() {
    return this.hasAttribute('autoplay');
  }

  getAutoplaySpeed() {
    return this.hasAttribute('autoplay')
      ? parseInt(this.getAttribute('autoplay-speed', 10), 10) * 1000
      : 5000;
  }

  handleBlockSelect(event) {
    this.carousel.slider.slideToLoop(this.getSlideItems.indexOf(event.target));
  }

  initializeCarousel() {
    if (this.getSlideItems.length > 1) {
      this.carousel = new FoxTheme.Carousel(
        this,
        {
          navigation: {
            nextEl: this.getNextSlideButton(),
            prevEl: this.getPrevSlideButton(),
          },
          loop: true,
          autoplay: this.isAutoplayEnabled()
            ? { delay: this.getAutoplaySpeed(), pauseOnMouseEnter: true }
            : false,
        },
        FoxTheme.Swiper.Autoplay
      );
      this.carousel && this.carousel.init();

      if (Shopify.designMode) {
        this.addEventListener('shopify:block:select', this.boundHandleBlockSelect);
      }
    }
  }

  // Cleanup: Remove event listener when element is removed from DOM
  disconnectedCallback() {
    if (Shopify.designMode) {
      this.removeEventListener('shopify:block:select', this.boundHandleBlockSelect);
    }
  }
}
customElements.define('announcement-bar', AnnouncementBar);

class SelectElement extends HTMLElement {
  constructor() {
    super();

    // Store bound handler for proper cleanup
    this.boundHandleSelectChange = this.handleSelectChange.bind(this);

    FoxTheme.Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });

    this.select = this.querySelector('select');

    this.select && this.select.addEventListener('change', this.boundHandleSelectChange);
  }

  init() {
    // Use requestAnimationFrame to batch DOM operations and prevent layout thrashing
    requestAnimationFrame(() => {
      const style = window.getComputedStyle(this);
      const value = this.select.options[this.select.selectedIndex].text;

      const text = document.createElement('span');
      // Use cssText for batched style assignment (more efficient than individual properties)
      text.style.cssText = `
        font-family: ${style.fontFamily};
        font-size: ${style.fontSize};
        font-weight: ${style.fontWeight};
        visibility: hidden;
        position: absolute;
      `;
      // Use textContent for plain text (safer than innerHTML)
      text.textContent = value;

      document.body.appendChild(text);
      const width = text.clientWidth;

      this.style.setProperty('--width', `${width}px`);
      text.remove();
    });
  }

  handleSelectChange() {
    this.init();
  }

  // Cleanup: Remove event listener when element is removed from DOM
  disconnectedCallback() {
    if (this.select) {
      this.select.removeEventListener('change', this.boundHandleSelectChange);
    }
  }
}
customElements.define('select-element', SelectElement);

class ProductBundleVariantSelector extends HTMLElement {
  constructor() {
    super();

    // Store bound handler for proper cleanup
    this.boundHandleVariantChange = this.handleVariantChange.bind(this);

    this.cssClasses = {
      soldOut: 'f-price--sold-out',
      onSale: 'f-price--on-sale',
      noCompare: 'f-price--no-compare',
    };

    this.priceElement = this.querySelector('.f-price');
    this.variantSelect = this.querySelector('select');

    this.variantSelect &&
      this.variantSelect.addEventListener('change', this.boundHandleVariantChange);
  }

  handleVariantChange(event) {
    const variantSelect = event.target;
    const selectedVariant = variantSelect.options[variantSelect.selectedIndex];
    const compareAtPrice = parseInt(selectedVariant.dataset.compare_at_price || 0, 10);
    const price = parseInt(selectedVariant.dataset.price || 0, 10);
    const available = selectedVariant.dataset.available === 'true';
    const priceVaries = variantSelect.dataset.price_varies === 'true';
    const compareAtPriceVaries = variantSelect.dataset.compare_at_price_varies === 'true';

    // Remove classes
    this.priceElement.classList.remove(this.cssClasses.soldOut);
    this.priceElement.classList.remove(this.cssClasses.onSale);
    this.priceElement.classList.remove(this.cssClasses.noCompare);

    // Add classes
    if (!available) {
      this.priceElement.classList.add(this.cssClasses.soldOut);
    } else if (compareAtPrice > price && available) {
      this.priceElement.classList.add(this.cssClasses.onSale);
    }
    if (!priceVaries && compareAtPriceVaries) {
      this.priceElement.classList.add(this.cssClasses.noCompare);
    }

    // Change price - use textContent for plain text, DOM methods for structured content
    const regularPriceElement = this.querySelector('.f-price__regular');
    regularPriceElement.querySelector('.f-price-item--regular').textContent =
      FoxTheme.Currency.formatMoney(price, FoxTheme.settings.moneyFormat);

    const salePriceElement = this.querySelector('.f-price__sale');
    // Build strikethrough price safely
    const regularItem = salePriceElement.querySelector('.f-price-item--regular');
    const s = document.createElement('s');
    s.textContent = FoxTheme.Currency.formatMoney(compareAtPrice, FoxTheme.settings.moneyFormat);
    regularItem.replaceChildren(s);

    salePriceElement.querySelector('.f-price-item--sale').textContent =
      FoxTheme.Currency.formatMoney(price, FoxTheme.settings.moneyFormat);
  }

  // Cleanup: Remove event listener when element is removed from DOM
  disconnectedCallback() {
    if (this.variantSelect) {
      this.variantSelect.removeEventListener('change', this.boundHandleVariantChange);
    }
  }
}
customElements.define('product-bundle-variant-selector', ProductBundleVariantSelector);

class ProductsBundle extends HTMLElement {
  constructor() {
    super();

    // Conditional logging setup
    const isDev =
      window.location.hostname.includes('myshopify.com') ||
      window.location.hostname === 'localhost';
    // eslint-disable-next-line no-console
    this.log = isDev ? console.log.bind(console) : () => {};
    this.warn = isDev ? console.warn.bind(console) : () => {};
    this.error = isDev ? console.error.bind(console) : () => {};

    // Store bound handlers for proper cleanup
    this.boundHandleButtonClick = this.handleButtonClick.bind(this);
    this.boundHandleBlockHoverEnter = this.handleBlockHover.bind(this, 'enter');
    this.boundHandleBlockHoverLeave = this.handleBlockHover.bind(this, 'leave');

    this.cssClasses = {
      hover: 'is-hover',
      active: 'is-active',
    };

    this.cartDrawerElement = document.querySelector('cart-drawer');
    this.addToCartButtonElement = this.querySelector('button');

    this.addToCartButtonElement &&
      this.addToCartButtonElement.addEventListener('click', this.boundHandleButtonClick);

    this.productBundleBlocks = this.querySelectorAll('[data-block-id]');
    this.productBundleBlocks.forEach((block) =>
      block.addEventListener('mouseenter', this.boundHandleBlockHoverEnter)
    );
    this.productBundleBlocks.forEach((block) =>
      block.addEventListener('mouseleave', this.boundHandleBlockHoverLeave)
    );
  }

  handleBlockHover(type, event) {
    if (type === 'enter') {
      this.classList.add(this.cssClasses.hover);
      this.toggleBlockActive(event.target, true);
    } else {
      this.classList.remove(this.cssClasses.hover);
      this.toggleBlockActive(null, false);
    }
  }

  toggleBlockActive(target, _isActive) {
    this.productBundleBlocks.forEach((block) => {
      if (target) {
        if (block.dataset.blockId === target.dataset.blockId) {
          block.classList.add(this.cssClasses.active);
        } else {
          block.classList.remove(this.cssClasses.active);
        }
      } else {
        block.classList.remove(this.cssClasses.active);
      }
    });
  }

  handleButtonClick(event) {
    event.preventDefault();
    this.lastClickedElement = event.submitter || event.currentTarget;

    const productIds = this.querySelectorAll('[name="id"]');
    const items = {
      items: [...productIds]
        .map((e) => e.value)
        .map((e) => ({
          id: e,
          quantity: 1,
        })),
    };

    if (
      document.body.classList.contains('cart-template') ||
      FoxTheme.settings.cartType !== 'drawer'
    ) {
      FoxTheme.utils.postLink(FoxTheme.routes.cart_add_url, {
        parameters: {
          ...items,
        },
      });
      return;
    }

    this.showErrorMessage();
    this.toggleButtonLoading(true);

    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle },
      })
    );

    const body = JSON.stringify({
      ...items,
      sections: sectionsToBundle,
      section_url: window.pathname,
    });

    fetch(`${FoxTheme.routes.cart_add_url}`, {
      ...FoxTheme.utils.fetchConfig('javascript'),
      ...{ body },
    })
      .then((response) => response.json())
      .then(async (parsedState) => {
        if (parsedState.status) {
          this.showErrorMessage(parsedState.description);
          return;
        }
        if (parsedState.status === 422) {
          document.dispatchEvent(
            new CustomEvent('product-ajax:error', {
              detail: {
                errorMessage: parsedState.description,
              },
            })
          );
        } else {
          const cartJson = await (
            await fetch(`${FoxTheme.routes.cart_url}`, { ...FoxTheme.utils.fetchConfig() })
          ).json();
          cartJson['sections'] = parsedState['sections'];

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
          document.dispatchEvent(
            new CustomEvent('ajaxProduct:added', {
              detail: {
                product: parsedState,
              },
            })
          );
          // Defer drawer show to prevent blocking UI thread during CSS transitions
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              this.cartDrawerElement && this.cartDrawerElement.show(this.lastClickedElement);
            });
          });
        }
      })
      .catch((e) => {
        this.error('[ProductsBundle] Failed to add bundle to cart:', e);
      })
      .finally(() => {
        this.toggleButtonLoading(false);
      });
  }

  showErrorMessage(errorMessage = false) {
    this.errorMessageElement =
      this.errorMessageElement || this.querySelector('.product-form__error-message');
    if (this.errorMessageElement) {
      this.errorMessageElement.hidden = !errorMessage;

      if (errorMessage) this.errorMessageElement.textContent = errorMessage;
    } else {
      // eslint-disable-next-line no-alert
      errorMessage && alert(errorMessage);
    }
  }

  toggleButtonLoading(status) {
    if (status) {
      this.addToCartButtonElement.classList.add('btn--loading');
      this.addToCartButtonElement.setAttribute('disabled', true);
    } else {
      this.addToCartButtonElement.classList.remove('btn--loading');
      this.addToCartButtonElement.removeAttribute('disabled');
    }
  }

  // Cleanup: Remove all event listeners when element is removed from DOM
  disconnectedCallback() {
    if (this.addToCartButtonElement) {
      this.addToCartButtonElement.removeEventListener('click', this.boundHandleButtonClick);
    }
    this.productBundleBlocks.forEach((block) => {
      block.removeEventListener('mouseenter', this.boundHandleBlockHoverEnter);
      block.removeEventListener('mouseleave', this.boundHandleBlockHoverLeave);
    });
  }
}
customElements.define('products-bundle', ProductsBundle);

class ProductRecentlyViewed extends HTMLElement {
  constructor() {
    super();

    if (FoxTheme.config.hasLocalStorage) {
      const productId = parseInt(this.dataset.productId, 10);
      const cookieName = 'sleektheme:recently-viewed';
      const items = JSON.parse(window.localStorage.getItem(cookieName) || '[]');

      if (items.includes(productId)) {
        items.splice(items.indexOf(productId), 1);
      }

      items.unshift(productId);

      window.localStorage.setItem(cookieName, JSON.stringify(items.slice(0, 20)));
    }
  }
}
customElements.define('product-recently-viewed', ProductRecentlyViewed);

class MotionElement extends HTMLElement {
  constructor() {
    super();
    // Timer ID for cleanup
    this.refreshTimerId = null;
  }

  connectedCallback() {
    if (FoxTheme.config.motionReduced) return;
    this.preInitialize();
    FoxTheme.Motion.inView(
      this,
      async () => {
        if (!this.isInstant && this.media) await FoxTheme.utils.imageReady(this.media);
        this.initialize();
      },
      { margin: '0px 0px -50px 0px' }
    );
  }

  get isHold() {
    return this.hasAttribute('hold');
  }

  get isInstant() {
    return this.hasAttribute('data-instantly');
  }

  get mediaElements() {
    return Array.from(this.querySelectorAll('img, iframe, svg'));
  }

  get animationType() {
    return this.dataset.motion || 'fade-up';
  }

  get animationDelay() {
    return parseInt(this.dataset.motionDelay || 0, 10) / 1000;
  }

  preInitialize() {
    if (this.isHold) return;
    switch (this.animationType) {
      case 'fade-in':
        FoxTheme.Motion.animate(this, { opacity: 0.01 }, { duration: 0 });
        break;

      case 'fade-up':
        FoxTheme.Motion.animate(
          this,
          { transform: 'translateY(2.5rem)', opacity: 0.01 },
          { duration: 0 }
        );
        break;

      case 'zoom-in':
        FoxTheme.Motion.animate(this, { transform: 'scale(0.8)' }, { duration: 0 });
        break;
      case 'zoom-in-lg':
        FoxTheme.Motion.animate(this, { transform: 'scale(0)' }, { duration: 0 });
        break;

      case 'zoom-out':
        FoxTheme.Motion.animate(this, { transform: 'scale(1.3)' }, { duration: 0 });
        break;

      case 'zoom-out-sm':
        FoxTheme.Motion.animate(this, { transform: 'scale(1.1)' }, { duration: 0 });
    }
  }

  async initialize() {
    if (this.isHold) return;

    switch (this.animationType) {
      case 'fade-in':
        await FoxTheme.Motion.animate(
          this,
          { opacity: 1 },
          { duration: 1.5, delay: this.animationDelay, easing: [0, 0, 0.3, 1] }
        ).finished;
        break;

      case 'fade-up':
        await FoxTheme.Motion.animate(
          this,
          { transform: 'translateY(0)', opacity: 1 },
          { duration: 0.5, delay: this.animationDelay, easing: [0, 0, 0.3, 1] }
        ).finished;
        break;

      case 'zoom-in':
        await FoxTheme.Motion.animate(
          this,
          { transform: 'scale(1)' },
          { duration: 1.3, delay: this.animationDelay, easing: [0, 0, 0.3, 1] }
        ).finished;
        break;

      case 'zoom-in-lg':
        await FoxTheme.Motion.animate(
          this,
          { transform: 'scale(1)' },
          { duration: 0.5, delay: this.animationDelay, easing: [0, 0, 0.3, 1] }
        ).finished;
        break;

      case 'zoom-out':
        await FoxTheme.Motion.animate(
          this,
          { transform: 'scale(1)' },
          { duration: 1.5, delay: this.animationDelay, easing: [0, 0, 0.3, 1] }
        ).finished;
        break;

      case 'zoom-out-sm':
        await FoxTheme.Motion.animate(
          this,
          { transform: 'scale(1)' },
          { duration: 1, delay: this.animationDelay, easing: [0, 0, 0.3, 1] }
        ).finished;
        break;
    }
  }

  async resetAnimation(duration) {
    switch (this.animationType) {
      case 'fade-in':
        await FoxTheme.Motion.animate(
          this,
          { opacity: 0 },
          {
            duration: duration ? duration : 1.5,
            delay: this.animationDelay,
            easing: duration ? 'none' : [0, 0, 0.3, 1],
          }
        ).finished;
        break;

      case 'fade-up':
        await FoxTheme.Motion.animate(
          this,
          { transform: 'translateY(2.5rem)', opacity: 0 },
          {
            duration: duration ? duration : 0.5,
            delay: this.animationDelay,
            easing: duration ? 'none' : [0, 0, 0.3, 1],
          }
        ).finished;
        break;

      case 'zoom-in':
        await FoxTheme.Motion.animate(
          this,
          { transform: 'scale(0)' },
          {
            duration: duration ? duration : 1.3,
            delay: this.animationDelay,
            easing: duration ? 'none' : [0, 0, 0.3, 1],
          }
        ).finished;
        break;

      case 'zoom-in-lg':
        await FoxTheme.Motion.animate(
          this,
          { transform: 'scale(0)' },
          {
            duration: duration ? duration : 1.3,
            delay: this.animationDelay,
            easing: duration ? 'none' : [0, 0, 0.3, 1],
          }
        ).finished;
        break;

      case 'zoom-out':
        await FoxTheme.Motion.animate(
          this,
          { transform: 'scale(0)' },
          {
            duration: duration ? duration : 1.3,
            delay: this.animationDelay,
            easing: duration ? 'none' : [0.16, 1, 0.3, 1],
          }
        ).finished;
        break;

      case 'zoom-out-sm':
        await FoxTheme.Motion.animate(
          this,
          { transform: 'scale(0)' },
          {
            duration: duration ? duration : 1.3,
            delay: this.animationDelay,
            easing: duration ? 'none' : [0.16, 1, 0.3, 1],
          }
        ).finished;
        break;
    }
  }

  refreshAnimation() {
    this.removeAttribute('hold');
    this.preInitialize();

    // Clear existing timer before setting new one
    if (this.refreshTimerId) {
      clearTimeout(this.refreshTimerId);
    }

    this.refreshTimerId = setTimeout(() => {
      this.initialize();
    }, 50); // Delay a bit to make animation re init properly.
  }

  // Cleanup: Remove timer when element is removed from DOM
  disconnectedCallback() {
    if (this.refreshTimerId) {
      clearTimeout(this.refreshTimerId);
      this.refreshTimerId = null;
    }
  }
}
customElements.define('motion-element', MotionElement);

class TabsComponent extends HTMLElement {
  constructor() {
    super();

    // Store bound handlers for proper cleanup
    this.boundOnTabClick = this.onTabClick.bind(this);
    this.boundHandleKeyDown = this.handleKeyDown.bind(this);

    this.selectors = {
      tabHeader: '[role="tablist"]',
      tabPanels: ['[role="tabpanel"]'],
      tabNavs: ['[role="tab"]'],
      tabSelect: 'tab-selector',
    };
    this.domNodes = FoxTheme.utils.queryDomNodes(this.selectors, this);

    this.selectedIndex = 0;
    this.selectedTab = this.domNodes.tabPanels[this.selectedIndex];
    this.scrollToActiveTab = this.dataset.scrollToActiveTab === 'true';
    this.scrollOffset = 20;
    this.headerEl = document.querySelector('.header-section header[is="sticky-header"]');

    this.init();
    this.setActiveTab(0);

    this.tabChange = new CustomEvent('tabChange', {
      bubbles: true,
      detail: { selectedTab: this.selectedTab },
    });
  }

  connectedCallback() {
    this.setActiveTab(0);
  }

  init = () => {
    this.domNodes.tabNavs.forEach((tab) => {
      tab.addEventListener('click', this.boundOnTabClick);
    });
    this.domNodes.tabHeader &&
      this.domNodes.tabHeader.addEventListener('keydown', this.boundHandleKeyDown);
    this.setAccessible();
  };

  static get observedAttributes() {
    return ['data-selected'];
  }

  get buttons() {
    return (this._buttons =
      this._buttons || Array.from(this.querySelectorAll('button[role="tab"]')));
  }

  getHeaderHeight() {
    let headerHeight = 0;

    if (this.headerEl) {
      const height = Math.round(this.headerEl.offsetHeight);
      if (this.headerEl.isAlwaysSticky) {
        headerHeight = height;
      } else {
        const isSticky = document.body.classList.contains(this.headerEl.classes.pinned);

        if (window.scrollY > this.offsetTop - this.scrollOffset) {
          headerHeight = height;
        }

        if (isSticky && window.scrollY >= this.offsetTop - this.scrollOffset - height) {
          headerHeight = height;
        }
      }
    }

    return headerHeight;
  }

  scrollToTop() {
    const offsetTop = this.offsetTop - this.scrollOffset - this.getHeaderHeight();
    window.scrollTo({ top: offsetTop, behavior: 'smooth' });
  }

  onTabClick(evt) {
    evt.preventDefault();
    const currentTab = evt.currentTarget;
    const index = currentTab ? Number(currentTab.dataset.index) : 0;
    this.setActiveTab(index);

    if (this.scrollToActiveTab) {
      this.scrollToTop();
    }
  }

  setActiveTab = (tabIndex) => {
    const { tabNavs, tabPanels, tabSelect } = this.domNodes;
    if (tabIndex !== -1) {
      const newHeader = tabNavs && tabNavs[tabIndex];
      const newTab = tabPanels && tabPanels[tabIndex];
      this.setAttribute('data-selected', tabIndex);

      tabNavs.forEach((nav) => nav.setAttribute('aria-selected', false));

      newHeader && newHeader.setAttribute('aria-selected', true);

      this.selectedIndex = tabIndex;
      this.selectedTab = newTab;

      this.dispatchEvent(
        new CustomEvent('tabChange', {
          bubbles: true,
          detail: { selectedIndex: tabIndex, selectedTab: newTab },
        })
      );

      if (tabSelect) {
        tabSelect.querySelector('select').value = tabIndex;
      }
    }
  };

  setAccessible() {
    const { tabNavs, tabPanels } = this.domNodes;
    tabNavs.forEach((tab, index) => {
      if (tab.id) tabPanels[index].setAttribute('aria-labelledby', tab.id);
      tab.setAttribute('aria-selected', index === 0);
      tab.setAttribute('data-index', index);
      if (index !== 0) {
        // tab.setAttribute('tabindex', -1);
      }
    });
    tabPanels.forEach((panel, index) => {
      if (panel.id) tabNavs[index].setAttribute('aria-controls', panel.id);
      panel.setAttribute('tabindex', 0);
    });
  }

  handleKeyDown(e) {
    const { tabNavs } = this.domNodes;
    if (e.keyCode === 39 || e.keyCode === 37) {
      tabNavs[this.selectedIndex].setAttribute('tabindex', -1);
      if (e.keyCode === 39) {
        this.selectedIndex++;
        // If we're at the end, go to the start.
        if (this.selectedIndex >= tabNavs.length) {
          this.selectedIndex = 0;
        }
        // Move left.
      } else if (e.keyCode === 37) {
        this.selectedIndex--;
        // If we're at the start, move to the end.
        if (this.selectedIndex < 0) {
          this.selectedIndex = tabNavs.length - 1;
        }
      }

      tabNavs[this.selectedIndex].setAttribute('tabindex', 0);
      tabNavs[this.selectedIndex].focus();
    }
  }

  getSelected() {
    return {
      index: this.selectedIndex,
      element: this.selectedTab,
    };
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'data-selected' && oldValue !== null && oldValue !== newValue) {
      const fromButton = this.buttons[parseInt(oldValue, 10)];
      const toButton = this.buttons[parseInt(newValue, 10)];
      this.transition(
        document.getElementById(fromButton.getAttribute('aria-controls')),
        document.getElementById(toButton.getAttribute('aria-controls'))
      );
    }
  }

  async transition(fromPanel, toPanel) {
    await FoxTheme.Motion.animate(
      fromPanel,
      { transform: ['translateY(0)', 'translateY(2rem)'], opacity: [1, 0] },
      { duration: 0.3 }
    ).finished;

    // eslint-disable-next-line require-atomic-updates
    fromPanel.hidden = true;
    toPanel.hidden = false;

    FoxTheme.Motion.animate(
      toPanel,
      { transform: ['translateY(2rem)', 'translateY(0)'], opacity: [0, 1] },
      { duration: 0.3 }
    ).finished;

    toPanel.querySelector('grid-list') && toPanel.querySelector('grid-list').showGridItems();
  }

  // Cleanup: Remove all event listeners when element is removed from DOM
  disconnectedCallback() {
    this.domNodes.tabNavs.forEach((tab) => {
      tab.removeEventListener('click', this.boundOnTabClick);
    });
    if (this.domNodes.tabHeader) {
      this.domNodes.tabHeader.removeEventListener('keydown', this.boundHandleKeyDown);
    }
  }
}
customElements.define('tabs-component', TabsComponent);

class TabSelector extends HTMLElement {
  constructor() {
    super();

    // Store bound handler and select element for proper cleanup
    this.boundHandleChange = this.handleChange.bind(this);
    this.tabs = this.closest('tabs-component');
    this.selectElement = this.querySelector('select');

    this.selectElement && this.selectElement.addEventListener('change', this.boundHandleChange);
  }

  handleChange(e) {
    this.tabs.setActiveTab(e.target.value);
  }

  // Cleanup: Remove event listener when element is removed from DOM
  disconnectedCallback() {
    if (this.selectElement) {
      this.selectElement.removeEventListener('change', this.boundHandleChange);
    }
  }
}
customElements.define('tab-selector', TabSelector);

class Parallax extends HTMLElement {
  get parallax() {
    return this.dataset.parallax ? parseFloat(this.dataset.parallax) : false;
  }

  get parallaxDirection() {
    return this.dataset.parallaxDirection || 'vertical';
  }

  get parallaxMediaElements() {
    return Array.from(this.querySelectorAll('img, video, iframe, svg, video-element'));
  }

  connectedCallback() {
    if (this.shouldInitializeParallax()) {
      this.setupParallax();
    }
  }

  shouldInitializeParallax() {
    return !FoxTheme.config.motionReduced && this.parallax;
  }

  setupParallax() {
    const parallaxScale = 1 + this.parallax;
    const parallaxTranslate = (this.parallax * 100) / (1 + this.parallax);

    let parallaxTransformProperties;

    if (this.parallaxDirection === 'vertical') {
      parallaxTransformProperties = {
        transform: [
          `scale(${parallaxScale}) translateY(0)`,
          `scale(${parallaxScale}) translateY(${parallaxTranslate}%)`,
        ],
        transformOrigin: ['bottom', 'bottom'],
      };
    } else if (this.parallaxDirection === 'horizontal') {
      parallaxTransformProperties = {
        transform: [
          `scale(${parallaxScale}) translateX(0)`,
          `scale(${parallaxScale}) translateX(${parallaxTranslate}%)`,
        ],
        transformOrigin: ['right', 'right'],
      };
    } else {
      parallaxTransformProperties = {
        transform: [`scale(1)`, `scale(${parallaxScale})`],
        transformOrigin: ['center', 'center'],
      };
    }

    FoxTheme.Motion.scroll(
      FoxTheme.Motion.animate(this.parallaxMediaElements, parallaxTransformProperties, {
        easing: 'linear',
      }),
      {
        target: this,
        offset: ['start end', 'end start'],
      }
    );
  }
}
customElements.define('parallax-element', Parallax);

class ProductForm extends HTMLFormElement {
  constructor() {
    super();

    // Conditional logging setup
    const isDev =
      window.location.hostname.includes('myshopify.com') ||
      window.location.hostname === 'localhost';
    // eslint-disable-next-line no-console
    this.log = isDev ? console.log.bind(console) : () => {};
    this.warn = isDev ? console.warn.bind(console) : () => {};
    this.error = isDev ? console.error.bind(console) : () => {};

    // Store bound handler for proper cleanup
    this.boundHandleFormSubmit = this.handleFormSubmit.bind(this);
    this.boundHandleQuickViewHide = this.handleQuickViewHide.bind(this);

    // Timer ID for cleanup
    this.quickViewHideTimerId = null;

    this.productIdInput = this.querySelector('[name=id]');
    this.productIdInput.disabled = false;

    // Enterprise Fix: Explicit validation bypass for Mobile Safari
    // Mobile Safari sometimes ignores novalidate attribute on forms with custom elements
    this.setAttribute('novalidate', '');
    const variantInputs = this.querySelectorAll('input[type="radio"][form], select[form]');
    variantInputs.forEach((input) => {
      input.removeAttribute('required');
      if (input.willValidate) {
        input.setCustomValidity('');
      }
    });

    this.addEventListener('submit', this.boundHandleFormSubmit);
  }

  get cartDrawerElement() {
    // Cache the cart drawer element to avoid repeated querySelectorAll
    // which can be blocking when called during form submission
    if (!this._cachedCartDrawer) {
      this._cachedCartDrawer = document.querySelector('cart-drawer');
    }
    return this._cachedCartDrawer;
  }

  get submitButtonElement() {
    return (this._submitButtonElement =
      this._submitButtonElement || this.querySelector('[type="submit"]'));
  }

  get isHideErrors() {
    return this.dataset.hideErrors === 'true';
  }

  handleFormSubmit = (event) => {
    if (
      document.body.classList.contains('cart-template') ||
      FoxTheme.settings.cartType === 'page'
    ) {
      return;
    }

    event.preventDefault();
    if (this.submitButtonElement.hasAttribute('aria-disabled')) return;

    // Prevent duplicate submissions
    if (this.isSubmitting) return;
    this.isSubmitting = true;
    this.hasError = false;

    // Enterprise Fix: Prevent out of stock submission (Enter key bypass)
    if (this.dataset.variantUnavailable === 'true' || this.submitButtonElement.disabled) {
      this.displayFormErrors('This product is currently unavailable');
      this.isSubmitting = false;
      return;
    }

    this.lastSubmittedElement = event.submitter || event.currentTarget;

    this.displayFormErrors();

    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle },
      })
    );

    // CRITICAL FIX: Wrap FormData construction in try/catch
    // If form construction fails, isSubmitting should be reset
    let config;
    let formData;
    try {
      config = FoxTheme.utils.fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      formData = new FormData(this);
      formData.append('sections', sectionsToBundle);
      formData.append('sections_url', window.location.pathname);

      config.body = formData;
    } catch (formError) {
      this.error('[ProductForm] Failed to construct form data:', formError);
      this.displayFormErrors('Unable to process form. Please try again.');
      this.submitButtonElement.removeAttribute('aria-disabled');
      this.submitButtonElement.classList.remove('btn--loading');
      this.isSubmitting = false;
      return;
    }

    this.submitButtonElement.setAttribute('aria-disabled', 'true');
    this.submitButtonElement.classList.add('btn--loading');

    // Enterprise Fix: Safety timeout to prevent stuck loading state
    this.submissionTimeout = setTimeout(() => {
      if (this.isSubmitting) {
        this.submitButtonElement.removeAttribute('aria-disabled');
        this.submitButtonElement.classList.remove('btn--loading');
        this.isSubmitting = false;
        this.displayFormErrors('Request timed out. Please try again.');
      }
    }, 30000); // 30 second timeout

    this.handleFormSubmission(config);
  };

  displayFormErrors = (errorMessage = false) => {
    if (this.isHideErrors) return;

    this.formErrorMessage =
      this.formErrorMessage || this.querySelector('.product-form__error-message');
    if (!this.formErrorMessage) return;

    this.formErrorMessage.toggleAttribute('hidden', !errorMessage);
    this.formErrorMessage.innerText = errorMessage;
  };

  handleFormSubmission = (config) => {
    fetch(`${FoxTheme.routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.status) {
          this.handleCartError(parsedState);
          return;
        }

        // CRITICAL FIX: Defer cart fetch to non-blocking callback
        // Previous code: await (await fetch(...)).json() blocked UI thread for 500-1500ms
        // This caused page freezes visible to user during add-to-cart
        // Solution: Use requestIdleCallback to fetch cart state WITHOUT blocking

        const deferredCartFetch = () => {
          // Fetch full cart state in background without blocking main thread
          fetch(`${FoxTheme.routes.cart_url}`, { ...FoxTheme.utils.fetchConfig() })
            .then((response) => response.json())
            .then((cartJson) => {
              if (!cartJson) {
                this.error('[ProductForm] Cart fetch returned empty');
                return;
              }

              // Merge sections from add response
              cartJson['sections'] = parsedState['sections'] || {};

              // Update cart state (persists to localStorage)
              this.updateCartState(cartJson);

              // Dispatch event for other listeners
              this.dispatchProductAddedEvent(parsedState);
            })
            .catch((cartFetchError) => {
              this.error('[ProductForm] Failed to fetch cart after add:', cartFetchError);
              // Don't block the add-to-cart - fallback to just the parsedState
              this.dispatchProductAddedEvent(parsedState);
            });
        };

        // iOS Safari considers the device "busy" after a tap gesture, causing
        // requestIdleCallback to defer for up to 2s — cart drawer shows stale
        // state then jumps when it finally updates. Use setTimeout on iOS.
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        if (!isIOS && 'requestIdleCallback' in window) {
          requestIdleCallback(deferredCartFetch, { timeout: 500 });
        } else {
          setTimeout(deferredCartFetch, 0);
        }

        // Show cart drawer immediately - don't wait for cart fetch
        this.showCartDrawer();
      })
      .catch((error) => {
        this.error('[ProductForm] Failed to add to cart:', error);

        // Enterprise Fix: Track error for monitoring
        if (window.ShopifyCartErrorTracker) {
          window.ShopifyCartErrorTracker.track(
            'add_to_cart_failed',
            error.message || 'Network error',
            {
              productId: this.productIdInput ? this.productIdInput.value : 'unknown',
              url: window.location.href,
              errorType: 'network_failure',
            }
          );
        }

        // Enterprise Fix: Show user-friendly error message for network failures
        this.displayFormErrors(
          'Unable to add to cart. Please check your connection and try again.'
        );

        // Enterprise Fix: Re-enable submit button so user can retry
        this.submitButtonElement.removeAttribute('aria-disabled');
        this.submitButtonElement.classList.remove('btn--loading');
        this.isSubmitting = false;
      })
      .finally(() => {
        // Enterprise Fix: Clear timeout to prevent false timeout errors
        if (this.submissionTimeout) {
          clearTimeout(this.submissionTimeout);
        }

        this.submitButtonElement.classList.remove('btn--loading');

        if (!this.hasError) {
          this.submitButtonElement.removeAttribute('aria-disabled');
        }

        // Enterprise Fix: Reset submission flag in finally block
        this.isSubmitting = false;
      });
  };

  handleCartError = (parsedState) => {
    FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartError, {
      source: 'product-form',
      productVariantId: this.productIdInput?.value,
      errors: parsedState.errors || parsedState.description,
      message: parsedState.message,
    });
    this.displayFormErrors(parsedState.description);
    document.dispatchEvent(
      new CustomEvent('product-ajax:error', {
        detail: {
          errorMessage: parsedState.description,
        },
      })
    );
    // Re-enable button so user can retry (change quantity/variant and resubmit)
    this.submitButtonElement.removeAttribute('aria-disabled');
    this.submitButtonElement.classList.remove('btn--loading');
    this.hasError = true;
  };

  updateCartState = (cartJson) => {
    FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
  };

  dispatchProductAddedEvent = (parsedState) => {
    document.dispatchEvent(
      new CustomEvent('product-ajax:added', {
        detail: {
          product: parsedState,
        },
      })
    );
  };

  handleQuickViewHide() {
    // Clear existing timer before setting new one
    if (this.quickViewHideTimerId) {
      clearTimeout(this.quickViewHideTimerId);
    }

    // Defer drawer show with requestAnimationFrame to prevent blocking UI thread
    // Use double rAF to ensure browser can complete all previous operations
    this.quickViewHideTimerId = undefined;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.cartDrawerElement && this.cartDrawerElement.show(this.lastSubmittedElement);
      });
    });
  }

  showCartDrawer = () => {
    const quickViewModal = this.closest('quick-view-modal');
    if (quickViewModal) {
      if (this.cartDrawerElement && !this.cartDrawerElement.open) {
        // Store reference to track if we added the listener
        this.hasQuickViewListener = true;
        document.body.addEventListener(
          quickViewModal.events.handleAfterHide,
          this.boundHandleQuickViewHide,
          { once: true }
        );
      }

      quickViewModal.hide(true);
    } else {
      // Defer drawer show to prevent blocking UI thread during CSS transitions
      // Use double requestAnimationFrame to ensure browser can repaint between operations
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.cartDrawerElement && this.cartDrawerElement.show(this.lastSubmittedElement);
        });
      });
    }
  };

  // Cleanup: Remove all event listeners when element is removed from DOM
  disconnectedCallback() {
    this.removeEventListener('submit', this.boundHandleFormSubmit);

    // Clean up timer
    if (this.quickViewHideTimerId) {
      clearTimeout(this.quickViewHideTimerId);
      this.quickViewHideTimerId = null;
    }

    // Note: The body listener uses {once: true} so it auto-removes, but clean up just in case
    if (this.hasQuickViewListener) {
      const quickViewModal = this.closest('quick-view-modal');
      if (quickViewModal) {
        document.body.removeEventListener(
          quickViewModal.events.handleAfterHide,
          this.boundHandleQuickViewHide
        );
      }
    }
  }
}
customElements.define('product-form', ProductForm, { extends: 'form' });

class HighlightText extends HTMLElement {
  constructor() {
    super();

    FoxTheme.Motion.inView(this, this.init.bind(this));
  }

  init() {
    this.classList.add('animate');
  }
}
customElements.define('highlight-text', HighlightText, { extends: 'em' });

document.addEventListener('change', function (event) {
  const target = event.target;
  if (!target || !target.classList.contains('recurpay__frequency_select')) return;

  const selectedOption = target.options[target.selectedIndex];
  if (!selectedOption) return;

  const productThumbsItems = document.querySelectorAll('.thumbnail-list .product__thumbs-item');
  if (productThumbsItems && productThumbsItems.length > 2) {
    const thirdItem = productThumbsItems[2];
    if (thirdItem && typeof thirdItem.click === 'function') {
      thirdItem.click();
    }
  }
});

// Recurpay custom code
let recurpay_timeoutId = null;
function handleMutations(mutationsList, observer) {
  const widget = document.querySelector('.recurpay__widget');
  if (!widget) return;

  if (recurpay_timeoutId) clearTimeout(recurpay_timeoutId);

  recurpay_timeoutId = setTimeout(() => {
    const select = widget.querySelector("select[name='recurpay-input']");
    if (!select) return;

    const option3 = select.querySelector("option[data-plan-name='3 Months']");
    const option6 = select.querySelector("option[data-plan-name='6 Months']");

    if (option3) {
      option3.dataset.recommendedFrequency = 'true';
      option3.selected = true;
    }

    if (option6) {
      option6.dataset.recommendedFrequency = 'false';
      option6.selected = false;
    }

    select.dispatchEvent(new Event('change', { bubbles: true }));
    observer.disconnect();
  }, 1000);
}
const recurpay_observer = new MutationObserver(handleMutations);

recurpay_observer.observe(document.body, {
  childList: true,
  subtree: true,
});

// Fixed: Clean up observer on page unload
window.addEventListener('pagehide', () => {
  if (recurpay_observer) {
    recurpay_observer.disconnect();
  }
});
